import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';

import { debounce } from 'lodash';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  CssBaseline,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
  Popover,
  Button,
  Divider,
  FormControlLabel,
  Slider,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import '../App.css';

import logger from '../logger.js';
import { bookmakerLogos } from '../assets/bookmakerLogos';
import GameLogModal from '../components/GameLogModal';
logger.log('About to import bookmakerLogos...');
try {
  const logos = require('../assets/bookmakerLogos');
  logger.log('Successfully loaded logos:', Object.keys(logos.bookmakerLogos || {}));
} catch (err) {
  logger.error('Failed to load bookmakerLogos:', err);
}

const DEEPLINK_SUPPORTED_BOOKS = [
  'fanduel',
  'draftkings',
  'betmgm',
  'caesars',
  'espn bet', 
  'hard rock',
  'novig',
  'fanatics' ,
  'prophet exchange' // Add this
  // Add more books as needed
];
// Bucket map for EV Age tint
const ageToBucket = (ageSec) => {
  if (ageSec < 60) return 0;
  if (ageSec < 120) return 1;
  if (ageSec < 180) return 2;
  if (ageSec < 240) return 3;
  if (ageSec < 300) return 4;
  return 5;
};

const bucketColors = [
  'rgba(0,255,0,0.75)',
  'rgba(0,255,0,0.6)',
  'rgba(0,255,0,0.45)',
  'rgba(0,255,0,0.3)',
  'rgba(0,255,0,0.15)',
  'transparent',
];

// Custom dark theme
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  components: {
    MuiInputLabel: { 
      styleOverrides: { 
        root: { 
          color: '#ffffff',
          fontSize: '0.875rem',
        } 
      } 
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
        },
      },
    },
    MuiMenu: { 
      styleOverrides: { 
        paper: { 
          backgroundColor: '#333333', 
          color: '#ffffff' 
        } 
      } 
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontSize: '0.875rem',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { 
        root: { 
          color: '#ffffff', 
          '&.Mui-checked': { color: '#ff1744' } 
        } 
      } 
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderRadius: 4,
          border: '1px solid #444',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: '#ff1744',
        },
        thumb: {
          backgroundColor: '#ff1744',
          '&:hover': {
            boxShadow: '0 0 0 8px rgba(255, 23, 68, 0.16)',
          },
        },
        track: {
          backgroundColor: '#ffffff',
        },
        rail: {
          backgroundColor: '#555555',
        },
        valueLabel: {
          backgroundColor: '#333333',
          color: '#ffffff',
          fontSize: '0.75rem',
        },
        markLabel: {
          color: '#ffffff',
          fontSize: '0.75rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#333333',
          color: '#ffffff',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
        },
      },
    },
  },
});

// Helper function for EV color
const getEVColor = (value) => {
  if (value == null) return '#ffffff';
  const val = value * 100;
  let r, g, b;
  if (val <= -10) {
    r = 255; g = 0; b = 0;
  } else if (val > -10 && val <= 0) {
    const t = (val + 10) / 10;
    r = 255; g = Math.round(255 * t); b = Math.round(255 * t);
  } else if (val > 0 && val <= 10) {
    const t = val / 10;
    r = Math.round(255 * (1 - t)); g = 255; b = Math.round(255 * (1 - t));
  } else {
    r = 0; g = 255; b = 0;
  }
  return `rgb(${r},${g},${b})`;
};

// Common style constants
const CELL_STYLES = {
  extendedBackground: {
    width: 'calc(100% + 16px)',
    height: 'calc(100% + 16px)',
    margin: '-8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    pl: '16px',
  },
  cellText: {
    fontSize: '0.875rem',
    whiteSpace: 'pre',
  },
  eventText: {
    fontSize: '0.875rem',
    paddingLeft: '8px',
  },
  secondaryText: {
    fontSize: '0.875rem',
    paddingLeft: '8px',
    color: '#BBBBBB',
  },
};

// Static column definitions
const STATIC_COLUMN_DEFINITIONS = [
 {
    field: 'eventShortName',
    headerName: 'Event',
    minWidth: 185,
    flex: 2,
    headerAlign: 'left',
    align: 'left',
    sortComparator: (v1, v2, param1, param2) => {
      // Sort by eventStart date instead of event name
      const date1 = param1.api.getCellValue(param1.id, 'eventStart');
      const date2 = param2.api.getCellValue(param2.id, 'eventStart');
      
      if (!date1 && !date2) return 0;
      if (!date1) return 1;
      if (!date2) return -1;
      
      return new Date(date1).getTime() - new Date(date2).getTime();
    },
  },
  {
    field: 'playerName',
    headerName: 'Bet Info',
    minWidth: 165,
    flex: 2,
    headerAlign: 'left',
    align: 'left',
  },
  {
    field: 'logourl',
    headerName: 'Book',
    minWidth: 100,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
  },
  {
    field: 'price',
    headerName: 'Line/Odds',
    minWidth: 120,
    flex: 1,
    sortable: false,
    headerAlign: 'left',
    align: 'left',
  },
  {
    field: 'roi',
    headerName: 'EV',
    minWidth: 70,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
  },
  {
    field: 'growthRate',
    headerName: 'Growth%',
    minWidth: 130,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
  },
  {
    field: 'lastROIChangeTime',
    headerName: 'EV Age',
    type: 'number',
    minWidth: 100,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
  },
  {
    field: 'kelly',
    headerName: 'Kelly',
    minWidth: 70,
    sortable: true,
    headerAlign: 'left',
    align: 'left',
    flex: 1,
  },
  {
    field: 'trackBet',
    headerName: 'Track',
    sortable: false,
    minWidth: 65,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
  },
  {
    field: 'hideBet',
    headerName: 'Hide',
    sortable: false,
    minWidth: 25,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
  },
  {
    field: 'deeplink',
    headerName: '',
    sortable: false,
    minWidth: 80,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
  },
];

// Header renderers
const HEADER_RENDERERS = {
  default: (text) => () => (
    <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
      {text}
    </Typography>
  ),
  withTooltip: (text, tooltip) => () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <Tooltip title={tooltip}>
        <InfoIcon sx={{ fontSize: text === 'Growth%' ? '16px' : '0.875rem', color: '#ffffff', mr: 0.5 }} />
      </Tooltip>
      <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
        {text}
      </Typography>
    </Box>
  ),
};

// Memoized renderCell components
const RoiCell = React.memo(({ value }) => {
  if (value == null) return '-';
  const val = value * 100; // Convert to percentage
  let r, g, b;

  // Color logic
  if (val <= -10) {
    r = 255;
    g = 0;
    b = 0;
  } else if (val > -10 && val <= 0) {
    const t = (val + 10) / 10;
    r = 255;
    g = Math.round(255 * t);
    b = Math.round(255 * t);
  } else if (val > 0 && val <= 10) {
    const t = val / 10;
    r = Math.round(255 * (1 - t));
    g = 255;
    b = Math.round(255 * (1 - t));
  } else {
    r = 0;
    g = 255;
    b = 0;
  }

  // Format with fixed width: 2 digits before decimal, 1 after, plus '%'
  const formatted = val.toFixed(1).padStart(4, ' ') + '%'; // e.g., "10.5%", " 8.1%"
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
      <Typography sx={{ color: `rgb(${r},${g},${b})`, fontSize: '0.875rem', whiteSpace: 'pre', textAlign: 'left' }}>
        {formatted}
      </Typography>
    </Box>
  );
});

const GrowthRateCell = React.memo(({ value }) => {
  if (value == null) return '-';
  const val = value * 100; // Convert to percentage
  let r, g, b;

  if (val <= 0) {
    r = 255;
    g = 255;
    b = 255;
  } else if (val > 0 && val <= 0.5) {
    const t = val / 0.5;
    r = Math.round(204 * (1 - t));
    g = 255;
    b = Math.round(204 * (1 - t));
  } else {
    r = 0;
    g = 255;
    b = 0;
  }

  return <span style={{ color: `rgb(${r},${g},${b})` }}>{val.toFixed(2)}%</span>;
});

const KellyCell = React.memo(({ value, bankroll, kellyFraction }) => {
  const baseKellyValue = (value ?? 0) * bankroll;
  const adjustedKellyValue = baseKellyValue * (kellyFraction ?? 1);
  return adjustedKellyValue == null ? '' : `$${Math.round(adjustedKellyValue)}`;
});

const RoiAgeCell = React.memo(({ ageSeconds, displayValue, snipeMaxAge }) => {
  if (!displayValue) return null;
  const bucket = ageToBucket(ageSeconds, snipeMaxAge);
  return (
    <Box sx={{
      backgroundColor: bucketColors[bucket],
      color: '#ffffff',
      borderRadius: 0,
      width: 'calc(100% + 16px)', // Extend beyond cell padding
      height: 'calc(100% + 16px)', // Extend beyond cell padding
      margin: '-8px', // Negative margin to counteract cell padding
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      fontSize: '0.875rem',
      transition: 'background-color 0.5s ease',
      pl: '16px', // Adjusted padding for text
    }}>
      <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'pre' }}>
        {displayValue}
      </Typography>
    </Box>
  );
});

const DeeplinkCell = React.memo(({ row, isAuthenticated, fetchDeeplink }) => {
  const [loading, setLoading] = useState(false);
  
  // Check if this book supports deeplinks
  const bookSupportsDeeplink = DEEPLINK_SUPPORTED_BOOKS.includes(
    row.marketSourceName?.toLowerCase()
  ); 

  
const handleBetClick = async () => {
  logger.log('=== BUTTON CLICKED ===');
  logger.log('Current state:', {
    isAuthenticated,
    marketLineId: row.marketLineId,
    bookSupportsDeeplink,
    row
  });
  
  if (!isAuthenticated || !row.marketLineId || !bookSupportsDeeplink) {
    logger.log('BLOCKED BY CONDITIONS:', {
      notAuth: !isAuthenticated,
      noMarketLineId: !row.marketLineId, 
      bookNotSupported: !bookSupportsDeeplink
    });
    return;
  }
  
  logger.log('Proceeding with deeplink fetch...');
  setLoading(true);
  
  try {
    logger.log('Calling fetchDeeplink with:', {
      marketLineId: row.marketLineId,
      points: row.points,
      marketSourceName: row.marketSourceName
    });
    
    const deeplink = await fetchDeeplink(
      row.marketLineId, 
      row.points, 
      row.marketSourceName
    );
    
    logger.log('Deeplink response:', deeplink);
    
if (deeplink) {
  logger.log('Opening deeplink:', deeplink);
  
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  if (isIOS) {
    // Break out of iframe and navigate in parent window
    window.top.location.href = deeplink;
  } else {
    window.open(deeplink, '_blank', 'width=1000,height=800,scrollbars=yes');
  }
} else {
  logger.error('No deeplink returned from API');
}
  } catch (error) {
    logger.error('Error in handleBetClick:', error);
    logger.error('Error details:', {
      message: error.message,
      stack: error.stack
    });
  } finally {
    setLoading(false);
    logger.log('=== CLICK HANDLER COMPLETE ===');
  }
};  
  const disabled = !row.marketLineId || !isAuthenticated || !bookSupportsDeeplink || loading;
  
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'left', justifyContent: 'flex-start' }}>
      <Button
        onClick={handleBetClick}
        disabled={disabled}
        size="small"
        sx={{
          color: disabled ? '#666666' : '#00ff00',  // Green if enabled
          backgroundColor: 'transparent',
          border: 'none',
          padding: '4px 8px',
          fontSize: '0.875rem',
          '&.Mui-disabled': { color: '#666666' },
        }}
      >
        {loading ? '...' : 'Bet'}
      </Button>
    </Box>
  );
});
  const TrackBetCell = React.memo(({ row, trackedBets, toggleTrack, trackBet, untrackBet, trackingInProgress, immediatelyTracked, isAuthenticated }) => {
  const id = row.id;
  const isTracked = trackedBets.some(bet => bet.id === id) || immediatelyTracked.has(id);
  const isDisabled = !trackBet || !untrackBet || trackingInProgress.has(id) || !isAuthenticated;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => {
          if (!isDisabled) {
            toggleTrack(row, trackBet, untrackBet);
          }
        }}
        size="small"
        sx={{ padding: 0, color: isTracked ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={isDisabled}
      >
        {isTracked ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
      </IconButton>
    </Box>
  );
});

const HideBetCell = React.memo(({ row, hiddenBets, setHiddenBets, toggleHide, showHiddenBets, isAuthenticated, hideMarket, processedBets }) => {
  const id = row.id;
  const marketId = row.betMarketId;
  
  let isHidden;
  if (hideMarket && marketId) {
    // Check if any bet in the market is hidden
    const marketBets = processedBets.filter(bet => bet.betMarketId === marketId);
    isHidden = marketBets.some(bet => hiddenBets.includes(bet.id));
  } else {
    isHidden = hiddenBets.includes(id);
  }
  
  const isDisabled = !isAuthenticated;
  
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => {
          if (!isDisabled) {
            toggleHide(row);
            
            if (hideMarket && marketId) {
              // Update all bets in market
              const marketBets = processedBets.filter(bet => bet.betMarketId === marketId);
              const marketBetIds = marketBets.map(bet => bet.id);
              
              const updated = isHidden
                ? hiddenBets.filter(x => !marketBetIds.includes(x))
                : [...new Set([...hiddenBets, ...marketBetIds])];
              
              setHiddenBets(updated);
              localStorage.setItem('hiddenBets', JSON.stringify(updated));
            } else {
              // Original single bet logic
              const updated = isHidden
                ? hiddenBets.filter(x => x !== id)
                : [...hiddenBets, id];
              setHiddenBets(updated);
              localStorage.setItem('hiddenBets', JSON.stringify(updated));
            }
          }
        }}
        size="small"
        sx={{ padding: 0, color: isHidden ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={(isHidden && !showHiddenBets) || isDisabled}
      >
        {isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
      </IconButton>
    </Box>
  );
});

// Mobile Bet Card Component
const MobileBetCard = React.memo(({ 
  bet, 
  bankroll, 
  kellyFraction, 
  trackedBets, 
  hiddenSet,
  hiddenMarketSet,
  showHiddenBets,
  toggleTrack,
  toggleHide,
  trackBet,
  untrackBet,
  trackingInProgress,
  associatedBets,
  isBestOdds,
  immediatelyTracked,
  isAuthenticated,
  fetchDeeplink,
}) => {
  const [expanded, setExpanded] = useState(false);
   const [loadingDeeplink, setLoadingDeeplink] = useState(false);
  const isTracked = trackedBets.some(tb => tb.id === bet.id) || immediatelyTracked.has(bet.id);
  const isHidden = hiddenSet.has(bet.id) || (bet.betMarketId && hiddenMarketSet.has(bet.betMarketId));
  const isTrackingDisabled = !trackBet || !untrackBet || trackingInProgress.has(bet.id) || !isAuthenticated;

  const kellyValue = bet.kelly ? Math.round(bet.kelly * bankroll * kellyFraction) : 0;
const bookSupportsDeeplink = DEEPLINK_SUPPORTED_BOOKS.includes(
  bet.marketSourceName?.toLowerCase()
);
  // Format event date
  const eventDate = bet.eventStart
    ? new Date(bet.eventStart).toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).replace(',', '')
    : '';

  return (
    <Card sx={{ 
      mb: 1.5, 
      border: '1px solid #333',
    }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header Row */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box flex={1}>
            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
              {bet.playerName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#BBB', fontSize: '0.75rem' }}>
              {bet.betTypeName}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => !isTrackingDisabled && toggleTrack(bet, trackBet, untrackBet)}
              disabled={isTrackingDisabled}
              sx={{ 
                p: 0.5,
                color: isTracked ? '#00ff00' : '#888',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-disabled': {
                  color: '#444',
                }
              }}
            >
              {isTracked ? <CheckBoxIcon sx={{ fontSize: 20 }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: 20 }} />}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                if (isAuthenticated) {
                  toggleHide(bet);
                }
              }}
              disabled={(isHidden && !showHiddenBets) || !isAuthenticated}
              sx={{ 
                p: 0.5,
                color: isHidden ? '#00ff00' : '#888',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-disabled': {
                  color: '#444',
                }
              }}
            >
              {isHidden ? <VisibilityIcon sx={{ fontSize: 20 }} /> : <VisibilityOffIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Stack>
        </Stack>

        {/* Event Info */}
        <Box mb={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            {/* League name */}
            {bet.leagueName && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem', mr: 0.5 }}>
                  {bet.leagueName}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '0.875rem', mx: 0.5 }}>
                  |
                </Typography>
              </>
            )}
            {/* Matchup with logos */}
            {(() => {
              const [awayTeam, homeTeam] = bet.eventShortName.split('@');
              return (
                <>
                  {bet.awayLogo && (
                    <img
                      src={bet.awayLogo}
                      alt={`${awayTeam} Logo`}
                      style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }}
                      loading="lazy"
                    />
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    {awayTeam}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '0.75rem', mx: '2px' }}>
                    @
                  </Typography>
                  {bet.homeLogo && (
                    <img
                      src={bet.homeLogo}
                      alt={`${homeTeam} Logo`}
                      style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }}
                      loading="lazy"
                    />
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    {homeTeam}
                  </Typography>
                </>
              );
            })()}
          </Box>
          <Typography variant="body2" sx={{ color: '#BBB', fontSize: '0.75rem' }}>
            {eventDate}
          </Typography>
        </Box>

        {/* Book and Line with X More button */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <img
              src={bookmakerLogos[bet.marketSourceName]}
              alt={bet.marketSourceName}
              style={{ width: 48, height: 48, objectFit: 'contain' }}
            />
            <Box sx={{ 
              backgroundColor: isBestOdds ? 'rgba(0, 255, 0, 0.2)' : 'transparent',
              px: 1,
              py: 0.5,
              borderRadius: 1,
            }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {bet.sidepoints} {bet.price < 0 ? bet.price : `+${bet.price}`}
              </Typography>
            </Box>
          </Stack>
          
          {/* X More button next to odds */}
          {associatedBets && associatedBets.length > 1 && (
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />}
              sx={{ minWidth: 'auto' }}
            >
              {associatedBets.length - 1} More
            </Button>
          )}
        </Stack>

        {/* Associated Bets Accordion - Right after the X More button */}
        {expanded && associatedBets && associatedBets.length > 1 && (
          <Box mb={1.5}>
            <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem', mb: 1, display: 'block' }}>
              Other Books:
            </Typography>
            {(() => {
              // Include the main bet in the list and sort all bets
              const allBets = associatedBets;
              
              // Parse and sort all bets by line first, then by price
              const parsedBets = allBets.map(bet => ({
                ...bet,
                parsedSide: bet.side || '',
                parsedLine: Number(bet.points) || 0,
              }));

              const isOver = parsedBets[0]?.parsedSide === 'Over';
              const orderedBets = parsedBets.sort((a, b) => {
                const lineA = a.parsedLine;
                const lineB = b.parsedLine;
                const priceA = a.price ?? -Infinity;
                const priceB = b.price ?? -Infinity;

                if (lineA !== lineB) {
                  return isOver ? lineA - lineB : lineB - lineA;
                }
                return priceB - priceA;
              });

              return orderedBets.slice(0, 15).map((assocBet, idx) => (
                <Stack 
                  key={idx} 
                  direction="row" 
                  alignItems="center" 
                  spacing={1} 
                  sx={{ 
                    mt: 1,
                    backgroundColor: assocBet.id === bet.id ? 'rgba(0, 150, 255, 0.2)' : 'transparent',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5
                  }}
                >
                  <img
                    src={bookmakerLogos[assocBet.marketSourceName]}
                    alt={assocBet.marketSourceName}
                    style={{ width: 32, height: 32, objectFit: 'contain' }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    {assocBet.sidepoints} {assocBet.price < 0 ? assocBet.price : `+${assocBet.price}`}
                  </Typography>
                </Stack>
              ));
            })()}
          </Box>
        )}

        {/* Key Metrics Row with Place Bet button */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Grid container spacing={2} sx={{ flex: 1 }}>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>
                EV
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: getEVColor(bet.roi),
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                {bet.roi ? `${(bet.roi * 100).toFixed(1)}%` : '-'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>
                Growth%
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: bet.growthRate > 0 ? '#00ff00' : '#ffffff',
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}
              >
                {bet.growthRate ? `${(bet.growthRate * 100).toFixed(2)}%` : '-'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>
                Kelly
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                ${kellyValue}
              </Typography>
            </Grid>
          </Grid>
          
{/* Place Bet button on the right */}



<Button
  size="small"
  variant="contained"
  onClick={async () => {
    if (!bet.marketLineId || !isAuthenticated || !bookSupportsDeeplink) return;
    
    setLoadingDeeplink(true);
    try {
      const deeplink = await fetchDeeplink(
        bet.marketLineId,
        bet.points,
        bet.marketSourceName
      );
if (deeplink) {
  // On iOS in an iframe, we need to use window.top to break out
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  if (isIOS) {
    // Break out of iframe and navigate in parent window
    window.top.location.href = deeplink;
  } else {
    window.open(deeplink, '_blank', 'width=1000,height=800,scrollbars=yes');
  }
} else {
  logger.error('No deeplink available');
}
    } catch (error) {
      logger.error('Error fetching deeplink:', error);
    } finally {
      setLoadingDeeplink(false);
    }
  }}
  disabled={!bet.marketLineId || !isAuthenticated || !bookSupportsDeeplink || loadingDeeplink}
  sx={{
    backgroundColor: bookSupportsDeeplink && isAuthenticated ? '#00ff00' : '#333',
    color: bookSupportsDeeplink && isAuthenticated ? '#000' : '#666',
    minWidth: 'auto',
    px: 2,
    ml: 2,
    '&:hover': { 
      backgroundColor: bookSupportsDeeplink && isAuthenticated ? '#00cc00' : '#333' 
    },
    '&.Mui-disabled': { 
      backgroundColor: '#333', 
      color: '#666' 
    },
  }}
>
  {loadingDeeplink ? '...' : 'Bet'}
</Button>

        </Stack>
      </CardContent>
    </Card>
  );
});

const BetsDataGrid = ({ bets, trackBet, untrackBet, trackedBets, isAuthenticated, apiKey, userState }) => {
  const theme = customTheme;
  const apiRef = useGridApiRef();

  // State initialization
  const [sortModel, setSortModel] = useState([{ field: 'growthRate', sort: 'desc' }]);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [bankroll, setBankroll] = useState(() => {
    const saved = localStorage.getItem('bankroll');
    return saved ? parseFloat(saved) : 1000;
  });
  const [kellyFraction, setKellyFraction] = useState(() => {
    const saved = localStorage.getItem('kellyFraction');
    return saved ? parseFloat(saved) : 1;
  });
  const [hideTracked, setHideTracked] = useState(() => {
    const saved = localStorage.getItem('hideTracked');
    return saved ? JSON.parse(saved) : true;
  });
  const [showHiddenBets, setShowHiddenBets] = useState(() => {
    const saved = localStorage.getItem('showHiddenBets');
    return saved ? JSON.parse(saved) : false;
  });
  const [hideMarket, setHideMarket] = useState(() => {
  const saved = localStorage.getItem('hideMarket');
  return saved ? JSON.parse(saved) : false;
});
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [snipeMinEV, setSnipeMinEV] = useState(() => {
    const saved = localStorage.getItem('snipeMinEV');
    return saved !== null && saved !== 'null' ? parseFloat(saved) : null;
  });
  const [snipeMaxAge, setSnipeMaxAge] = useState(() => {
    const saved = localStorage.getItem('snipeMaxAge');
    return saved !== null && saved !== 'null' ? parseInt(saved) : null;
  });
  const [minEVDisplay, setMinEVDisplay] = useState('');
  const [maxAgeDisplay, setMaxAgeDisplay] = useState('');
  const [minGrowth, setMinGrowth] = useState(() => {
  const saved = localStorage.getItem('minGrowth');
  return saved !== null && saved !== 'null' ? parseFloat(saved) : null;
});
const [minGrowthDisplay, setMinGrowthDisplay] = useState('');
  const [breakEvenWinRange, setBreakEvenWinRange] = useState(() => {
    const saved = localStorage.getItem('breakEvenWinRange');
    return saved ? JSON.parse(saved) : [0, 100];
  });
  const [leagueFilter, setLeagueFilter] = useState(() => {
    const saved = localStorage.getItem('leagueFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [categoryFilter, setCategoryFilter] = useState(() => {
    const saved = localStorage.getItem('categoryFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [betTypeFilter, setBetTypeFilter] = useState(() => {
    const saved = localStorage.getItem('betTypeFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [bookFilter, setBookFilter] = useState(() => {
    const saved = localStorage.getItem('bookFilter');
    return saved ? JSON.parse(saved) : [];
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState(() => {
    const saved = localStorage.getItem('dateRange');
    return saved ? (saved === 'null' ? null : Number(saved)) : 7;
  });
  const [hiddenBets, setHiddenBets] = useState(() => {
    const s = localStorage.getItem('hiddenBets');
    const parsed = s ? JSON.parse(s) : [];
    return parsed.slice(-1000);
  });
  const [hiddenMarketIds, setHiddenMarketIds] = useState(() => {
    const s = localStorage.getItem('hiddenMarketIds');
    const parsed = s ? JSON.parse(s) : [];
    return parsed.slice(-1000);
  });
  const [pendingRemoval, setPendingRemoval] = useState(new Set());
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  
  // Track expandedRowIds in a ref for synchronous access
  const expandedRowIdsRef = useRef(new Set());
  
  // Track whether filters changed this render for getDetailPanelContent
  const filtersChangedThisRender = useRef(false);
  
  // Keep ref in sync with state
  useEffect(() => {
    expandedRowIdsRef.current = expandedRowIds;
  }, [expandedRowIds]);
  
  // Track previous filter values to detect changes synchronously
  const prevFiltersRef = useRef({
    leagueFilter,
    categoryFilter,
    betTypeFilter,
    bookFilter,
    searchQuery,
    dateRange,
    hideTracked,
    showHiddenBets,
    snipeMinEV,
    snipeMaxAge,
    minGrowth,
    breakEvenWinRange
  });
  
  // Detect if filters changed THIS render (synchronously)
  const filtersChanged = 
    prevFiltersRef.current.leagueFilter !== leagueFilter ||
    prevFiltersRef.current.categoryFilter !== categoryFilter ||
    prevFiltersRef.current.betTypeFilter !== betTypeFilter ||
    prevFiltersRef.current.bookFilter !== bookFilter ||
    prevFiltersRef.current.searchQuery !== searchQuery ||
    prevFiltersRef.current.dateRange !== dateRange ||
    prevFiltersRef.current.hideTracked !== hideTracked ||
    prevFiltersRef.current.showHiddenBets !== showHiddenBets ||
    prevFiltersRef.current.snipeMinEV !== snipeMinEV ||
    prevFiltersRef.current.snipeMaxAge !== snipeMaxAge ||
    prevFiltersRef.current.minGrowth !== minGrowth ||
    prevFiltersRef.current.breakEvenWinRange !== breakEvenWinRange;
  
  // Update ref for next render AND clear expanded rows synchronously
  if (filtersChanged) {
    logger.log('[FILTERS] Detected filter change synchronously - clearing expanded rows');
    
    // CRITICAL: Clear the ref IMMEDIATELY (synchronous)
    expandedRowIdsRef.current = new Set();
    
    // Mark that filters changed this render
    filtersChangedThisRender.current = true;
    
    // Schedule state update for next tick
    if (expandedRowIds.size > 0) {
      Promise.resolve().then(() => {
        setExpandedRowIds(new Set());
        // Clear the filter changed flag after state updates
        filtersChangedThisRender.current = false;
      });
    } else {
      // If no rows were expanded, clear the flag on next render
      Promise.resolve().then(() => {
        filtersChangedThisRender.current = false;
      });
    }
    
    prevFiltersRef.current = {
      leagueFilter,
      categoryFilter,
      betTypeFilter,
      bookFilter,
      searchQuery,
      dateRange,
      hideTracked,
      showHiddenBets,
      snipeMinEV,
      snipeMaxAge,
      minGrowth,
      breakEvenWinRange
    };
  }
  
  // detailPanelsDisabled should allow expansions - only used for optimization
  const detailPanelsDisabled = false;
  
  // Debug logging for expandedRowIds changes
  useEffect(() => {
    logger.log('[EXPANDED] expandedRowIds changed:', Array.from(expandedRowIds));
  }, [expandedRowIds]);
  const [trackingInProgress, setTrackingInProgress] = useState(new Set());
  const [immediatelyTracked, setImmediatelyTracked] = useState(new Set());
  
    const [gameLogModalOpen, setGameLogModalOpen] = useState(false);
  const [selectedPlayerData, setSelectedPlayerData] = useState(null);
  const [gameLogData, setGameLogData] = useState(null);
  const [gameLogLoading, setGameLogLoading] = useState(false);
  const [gameLogSport, setGameLogSport] = useState('NFL');
  const gridContainerRef = useRef(null);



  useEffect(() => {
    const gridContainer = gridContainerRef.current;
    if (!isAuthenticated && gridContainer) {
      const preventScroll = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };

      // Block scroll and drag-related events
      gridContainer.addEventListener('wheel', preventScroll, { passive: false });
      gridContainer.addEventListener('scroll', preventScroll, { passive: false });
      gridContainer.addEventListener('touchmove', preventScroll, { passive: false });
      gridContainer.addEventListener('mousedown', (e) => {
        // Block mousedown on scrollbar or grid content
        if (e.target.closest('.MuiDataGrid-virtualScroller') || e.target.closest('.MuiDataGrid-scrollbarFiller')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, { passive: false });
      gridContainer.addEventListener('mousemove', (e) => {
        // Block mousemove during drag attempts
        if (e.target.closest('.MuiDataGrid-virtualScroller') || e.target.closest('.MuiDataGrid-scrollbarFiller')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, { passive: false });

      return () => {
        gridContainer.removeEventListener('wheel', preventScroll);
        gridContainer.removeEventListener('scroll', preventScroll);
        gridContainer.removeEventListener('touchmove', preventScroll);
        gridContainer.removeEventListener('mousedown', preventScroll);
        gridContainer.removeEventListener('mousemove', preventScroll);
      };
    }
  }, [isAuthenticated]);
  const filterRef = useRef(null);
  const leagueInitialized = useRef(false);
  const categoryInitialized = useRef(false);
  const betTypeInitialized = useRef(false);
  const bookInitialized = useRef(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [currentTime, setCurrentTime] = useState(Date.now());

const fetchDeeplink = useCallback(async (marketLineId, points, marketSourceName) => {
  try {
    const response = await fetch('https://ws.betpros.ai:8080/api/deeplink', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        marketLineId,
        points,
        marketSourceName,
        isMobile: screenWidth < 768,
        userState: userState || ''
      })
    });
    
    const data = await response.json();
    return data.url;
  } catch (error) {
    logger.error('Error fetching deeplink:', error);
    return null;
  }
}, [screenWidth, userState]);
  // Refs for immediate state access to prevent race conditions
  const trackingStateRef = useRef(new Set());
  const trackedBetsRef = useRef([]);

  // Update refs when state changes and sync immediately tracked
  useEffect(() => {
    trackedBetsRef.current = trackedBets;
    
    // Clean up immediately tracked set when actual tracked bets update
    const currentTrackedIds = new Set(trackedBets.map(bet => bet.id));
    setImmediatelyTracked(prev => {
      const filtered = new Set([...prev].filter(id => !currentTrackedIds.has(id)));
      return filtered.size !== prev.size ? filtered : prev;
    });
  }, [trackedBets]);

  useEffect(() => {
    trackingStateRef.current = trackingInProgress;
  }, [trackingInProgress]);

  // Monitor for duplicate bets
  useEffect(() => {
    const ids = trackedBets.map(bet => bet.id);
    const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index);
    
    if (duplicates.length > 0) {
      logger.error('DUPLICATE BETS DETECTED:', duplicates);
      logger.error('All tracked bet IDs:', ids);
      logger.error('Tracked bets:', trackedBets);
    }
  }, [trackedBets]);



  // Enhanced untrackBet with logging
  const safeguardedUntrackBet = useCallback((betId) => {
    if (!isAuthenticated) {
      return;
    }
    
    // Check if bet exists before untracking
    if (!trackedBetsRef.current.some(bet => bet.id === betId)) {
      logger.warn(`[UNTRACK] Bet ${betId} not found in tracked bets`);
      return;
    }
    
    // Remove from immediately tracked
    setImmediatelyTracked(prev => {
      const next = new Set(prev);
      next.delete(betId);
      return next;
    });
    
    // Call the original untrackBet function
    if (untrackBet) {
      untrackBet(betId);
    }
  }, [untrackBet, isAuthenticated]);

  // Persist state changes to localStorage
  useEffect(() => {
    localStorage.setItem('bankroll', bankroll.toString());
  }, [bankroll]);

  useEffect(() => {
    localStorage.setItem('kellyFraction', kellyFraction.toString());
  }, [kellyFraction]);

  useEffect(() => {
    localStorage.setItem('hideTracked', JSON.stringify(hideTracked));
  }, [hideTracked]);

  useEffect(() => {
    localStorage.setItem('showHiddenBets', JSON.stringify(showHiddenBets));
  }, [showHiddenBets]);

  useEffect(() => {
    localStorage.setItem('hiddenMarketIds', JSON.stringify(hiddenMarketIds));
  }, [hiddenMarketIds]);

  useEffect(() => {
  localStorage.setItem('hideMarket', JSON.stringify(hideMarket));
  }, [hideMarket]);

  useEffect(() => {
    localStorage.setItem('snipeMinEV', JSON.stringify(snipeMinEV));
  }, [snipeMinEV]);
useEffect(() => {
  localStorage.setItem('minGrowth', JSON.stringify(minGrowth));
}, [minGrowth]);
  useEffect(() => {
    localStorage.setItem('snipeMaxAge', JSON.stringify(snipeMaxAge));
  }, [snipeMaxAge]);

  useEffect(() => {
    localStorage.setItem('breakEvenWinRange', JSON.stringify(breakEvenWinRange));
  }, [breakEvenWinRange]);

  useEffect(() => {
    localStorage.setItem('leagueFilter', JSON.stringify(leagueFilter));
  }, [leagueFilter]);

  useEffect(() => {
    localStorage.setItem('categoryFilter', JSON.stringify(categoryFilter));
  }, [categoryFilter]);

  useEffect(() => {
    localStorage.setItem('betTypeFilter', JSON.stringify(betTypeFilter));
  }, [betTypeFilter]);

  useEffect(() => {
    localStorage.setItem('bookFilter', JSON.stringify(bookFilter));
  }, [bookFilter]);

  useEffect(() => {
    localStorage.setItem('dateRange', JSON.stringify(dateRange));
  }, [dateRange]);

  useEffect(() => {
    setMinEVDisplay(snipeMinEV ?? 'No Min');
  }, [snipeMinEV]);
useEffect(() => {
  setMinGrowthDisplay(minGrowth ?? 'No Min');
}, [minGrowth]);
  useEffect(() => {
    setMaxAgeDisplay(snipeMaxAge ?? 'No Max');
  }, [snipeMaxAge]);

// Update currentTime every second for EV Age recalculation (desktop only)
useEffect(() => {
  // Only update time on desktop where EV Age column is visible
  if (screenWidth >= 768) {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }
}, [screenWidth]);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Process bets
  const processedBets = useMemo(() => {
    if (!Array.isArray(bets)) return [];
    
return bets
  .filter(bet => {
    // Existing filters
    if (bet.pickem) return false;
    if (bet.roi == null || bet.roi > 0.1) return false;
    if (bet.recentStatusChangeCount >= 2) return false; 
    if (['Stolen Bases', 'Hits', 'Pitcher Outs', 'Pitcher Hits Allowed', 
         'Pitcher Earned Runs', 'Player Singles'].includes(bet.betTypeName)) return false;  
    
    return true;
  })
  .map((row, idx) => {
  // Mutate the existing object instead of cloning
  if (!row.id) {
    row.id = `${idx}-${Date.now()}`;
  }
  
  let side = row.side || '';
  if (side === 'Over') side = 'o';
  else if (side === 'Under') side = 'u';
  row.sidepoints = `${side}${row.points || ''}`.trim();
  
  return row;  // ✅ Return same object, no clone
});
  }, [bets]);

    // Enhanced trackBet with safeguards
const safeguardedTrackBet = useCallback((bet) => {
  if (!isAuthenticated) {
    return;
  }
  
  // Check for duplicates before tracking
  if (trackedBetsRef.current.some(existing => existing.id === bet.id)) {
    logger.error(`[TRACK] DUPLICATE PREVENTED: Bet ${bet.id} already tracked`);
    return;
  }
  
  // Add to immediately tracked for instant hiding - ONLY this bet, not the whole market
  setImmediatelyTracked(prev => new Set(prev).add(bet.id));
  
  // Call the original trackBet function
  if (trackBet) {
    trackBet(bet);
  }
}, [trackBet, isAuthenticated]);

  // Enhanced toggle track function with stronger safeguards
  const toggleTrack = useCallback((row, trackBetFunc, untrackBetFunc) => {
    if (!isAuthenticated) {
      return;
    }
    
    const id = row.id;
    const marketId = row.betMarketId;
    // Check both tracking state and pending operations using refs for immediate access
    if (trackingStateRef.current.has(id)) {
      return;
    }

    // CRITICAL FIX: Close detail panel synchronously using apiRef (same as filters)
    // This prevents crashes when the tracked bet gets filtered out
    if (apiRef.current?.setExpandedDetailPanels) {
      const currentExpanded = apiRef.current.getExpandedDetailPanels();
      if (currentExpanded && currentExpanded.has && currentExpanded.has(id)) {
        const newExpanded = new Set(currentExpanded);
        newExpanded.delete(id);
        apiRef.current.setExpandedDetailPanels(newExpanded);
      }
    }
    // Also update React state
    if (expandedRowIds.has(id)) {
      setExpandedRowIds(new Set([...expandedRowIds].filter(rid => rid !== id)));
    }

    // Update tracking state immediately using refs
    const newTrackingState = new Set(trackingStateRef.current);
    newTrackingState.add(id);
    trackingStateRef.current = newTrackingState;
    setTrackingInProgress(newTrackingState);

    // Use current state from ref to determine action
    const alreadyTracked = trackedBetsRef.current.some(bet => bet.id === id);
    
if (alreadyTracked) {
  safeguardedUntrackBet(id);
} else {
  safeguardedTrackBet(row);  // Only tracks this specific bet
  
  if (hideTracked) {
    if (hideMarket && marketId) {
      // Get all OTHER bets in the market (excluding the one we just tracked)
      const marketBets = processedBets.filter(bet => 
        bet.betMarketId === marketId && bet.id !== id
      );
      const marketBetIds = marketBets.map(bet => bet.id);
      
      // Add only the OTHER market bets to hiddenBets (not the tracked one)
      if (marketBetIds.length > 0) {
        setHiddenBets(prev => {
          const newHiddenBets = [...new Set([...prev, ...marketBetIds])];
          localStorage.setItem('hiddenBets', JSON.stringify(newHiddenBets));
          return newHiddenBets;
        });
        
        // Only animate removal if showHiddenBets is false
        if (!showHiddenBets) {
          marketBets.forEach(bet => {
            setPendingRemoval(prev => new Set(prev).add(bet.id));
          });
          setTimeout(() => {
            marketBets.forEach(bet => {
              setPendingRemoval(prev => {
                const next = new Set(prev);
                next.delete(bet.id);
                return next;
              });
            });
          }, 200);
        }
      }
    }
    
    // The tracked bet itself gets hidden via normal hideTracked logic
    setPendingRemoval(prev => new Set(prev).add(id));
    setTimeout(() => {
      setPendingRemoval(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 200);
  }
}

    // Clear tracking progress with increased timeout
    setTimeout(() => {
      const clearedState = new Set(trackingStateRef.current);
      clearedState.delete(id);
      trackingStateRef.current = clearedState;
      setTrackingInProgress(clearedState);
    }, 500);

}, [safeguardedTrackBet, safeguardedUntrackBet, hideTracked, isAuthenticated, hideMarket, processedBets, showHiddenBets, expandedRowIds, apiRef]);

  // Enhanced debouncing with more aggressive settings
  const debouncedToggleTrack = useCallback(
    debounce(toggleTrack, 100, { 
      leading: true, 
      trailing: false,
      maxWait: 200 // Prevents burst clicking from queuing multiple calls
    }), 
    [toggleTrack]
  );

const toggleHide = useCallback((row) => {
  if (!isAuthenticated) {
    return;
  }
  
  const id = row.id;
  const marketId = row.betMarketId;
  
  // CRITICAL FIX: Close detail panel synchronously using apiRef (same as filters)
  // This prevents crashes when the hidden bet gets filtered out
  if (apiRef.current?.setExpandedDetailPanels) {
    const currentExpanded = apiRef.current.getExpandedDetailPanels();
    if (currentExpanded && currentExpanded.has && currentExpanded.has(id)) {
      const newExpanded = new Set(currentExpanded);
      newExpanded.delete(id);
      apiRef.current.setExpandedDetailPanels(newExpanded);
    }
  }
  // Also update React state
  if (expandedRowIds.has(id)) {
    setExpandedRowIds(new Set([...expandedRowIds].filter(rid => rid !== id)));
  }
  
  if (hideMarket && marketId) {
    // Hide/unhide by market ID
    const isMarketHidden = hiddenMarketIds.includes(marketId);
    
    if (isMarketHidden) {
      // Unhide the market
      const updated = hiddenMarketIds.filter(mId => mId !== marketId);
      setHiddenMarketIds(updated);
      localStorage.setItem('hiddenMarketIds', JSON.stringify(updated));
    } else {
      // Hide the market
      const newHiddenMarketIds = [...hiddenMarketIds, marketId];
      setHiddenMarketIds(newHiddenMarketIds);
      localStorage.setItem('hiddenMarketIds', JSON.stringify(newHiddenMarketIds));
      
      if (!showHiddenBets) {
        // Get all bets in this market for pending removal animation
        const marketBets = processedBets.filter(bet => bet.betMarketId === marketId);
        marketBets.forEach(bet => {
          setPendingRemoval(prev => new Set(prev).add(bet.id));
          setTimeout(() => {
            setPendingRemoval(prev => {
              const next = new Set(prev);
              next.delete(bet.id);
              return next;
            });
          }, 200);
        });
      }
    }
  } else {
    // Original single bet hide logic
    const isHidden = hiddenBets.includes(id);
    if (!isHidden && !showHiddenBets) {
      setPendingRemoval(prev => new Set(prev).add(id));
      setTimeout(() => {
        setPendingRemoval(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, 200);
    }
  }
}, [hiddenBets, hiddenMarketIds, showHiddenBets, isAuthenticated, hideMarket, processedBets, expandedRowIds, apiRef]);



  // Calculate filter options only when processedBets changes
  // Calculate filter options only when processedBets changes
  // Include both current data AND selected filters to persist selections
  const filterOptions = useMemo(() => {
    const leagues = new Set();
    const categories = new Set();
    const betTypes = new Set();
    const books = new Set();
    
    processedBets.forEach(bet => {
      if (bet.leagueName) leagues.add(bet.leagueName);
      if (bet.betCategory) categories.add(bet.betCategory);
      if (bet.betTypeName) betTypes.add(bet.betTypeName);
      if (bet.marketSourceName && !['bookmaker', 'pinnacle'].includes(bet.marketSourceName.toLowerCase())) {
        books.add(bet.marketSourceName);
      }
    });
    
    // Add currently selected filters to ensure they remain visible even if not in current data
    leagueFilter.forEach(item => leagues.add(item));
    categoryFilter.forEach(item => categories.add(item));
    betTypeFilter.forEach(item => betTypes.add(item));
    bookFilter.forEach(item => books.add(item));
    
    return {
      leagues: Array.from(leagues).sort(),
      categories: Array.from(categories).sort(),
      betTypes: Array.from(betTypes).sort(),
      books: Array.from(books).sort(),
    };
  }, [processedBets, leagueFilter, categoryFilter, betTypeFilter, bookFilter]);
  // Pre-compute filter Sets and other filter data
  const filterData = useMemo(() => {
    return {
      trackedSet: new Set(trackedBets.map(bet => bet.id)),
      immediatelyTrackedSet: new Set(immediatelyTracked),
      hiddenSet: new Set(hiddenBets),
      hiddenMarketSet: new Set(hiddenMarketIds),
      pendingRemovalSet: new Set(pendingRemoval),
      leagueSet: new Set(leagueFilter),
      categorySet: new Set(categoryFilter),
      betTypeSet: new Set(betTypeFilter),
      bookSet: new Set(bookFilter),
      searchTerms: searchQuery.toLowerCase().split(/\s+/).filter(term => term),
      hasLeagueFilter: leagueFilter.length > 0,
      hasCategoryFilter: categoryFilter.length > 0,
      hasBetTypeFilter: betTypeFilter.length > 0,
      hasBookFilter: bookFilter.length > 0,
      hasSearchFilter: searchQuery.trim().length > 0,
    };
  }, [trackedBets, immediatelyTracked, hiddenBets, hiddenMarketIds, pendingRemoval, leagueFilter, categoryFilter, betTypeFilter, bookFilter, searchQuery]);

  // Optimized filtered bets calculation
  const filteredBets = useMemo(() => {
    if (!processedBets.length) {
      return [];
    }

    const {
      trackedSet, immediatelyTrackedSet, hiddenSet, hiddenMarketSet, pendingRemovalSet,
      leagueSet, categorySet, betTypeSet, bookSet, searchTerms,
      hasLeagueFilter, hasCategoryFilter, hasBetTypeFilter, hasBookFilter, hasSearchFilter
    } = filterData;

    const filtered = processedBets.filter(bet => {
      // Early return for bookmaker/pinnacle
      if (['bookmaker', 'pinnacle'].includes(bet.marketSourceName?.toLowerCase())) {
        return false;
      }

      // Early return for tracked bets (if hideTracked is on)
      if (hideTracked && (trackedSet.has(bet.id) || immediatelyTrackedSet.has(bet.id)) && !pendingRemovalSet.has(bet.id)) {
        return false;
      }

      // Early return for hidden bets - check both individual bet IDs and market IDs
      if (!showHiddenBets && !pendingRemovalSet.has(bet.id)) {
        if (hiddenSet.has(bet.id) || (bet.betMarketId && hiddenMarketSet.has(bet.betMarketId))) {
          return false;
        }
      }

      // Filter checks - early return on first failure
      if (hasLeagueFilter && bet.leagueName && !leagueSet.has(bet.leagueName)) return false;
      if (hasCategoryFilter && bet.betCategory && !categorySet.has(bet.betCategory)) return false;
      if (hasBetTypeFilter && bet.betTypeName && !betTypeSet.has(bet.betTypeName)) return false;
      if (hasBookFilter && bet.marketSourceName && !bookSet.has(bet.marketSourceName)) return false;

      // Numeric filters
      if (snipeMinEV !== null && (bet.roi == null || bet.roi <= snipeMinEV / 100)) return false;
      if (minGrowth !== null && (bet.growthRate == null || bet.growthRate <= minGrowth / 100)) return false;

      if (snipeMaxAge !== null && bet.lastROIChangeTime) {
        const ageMinutes = (Date.now() - new Date(bet.lastROIChangeTime).getTime()) / 60000;
        if (ageMinutes >= snipeMaxAge) return false;
      }

      if (dateRange !== null && bet.eventStart) {
        const eventStartTime = new Date(bet.eventStart).getTime();
        const maxTime = Date.now() + dateRange * 24 * 60 * 60 * 1000;
        if (eventStartTime > maxTime) return false;
      }

      if (bet.breakEvenWin !== null) {
        const breakEvenPercent = bet.breakEvenWin * 100;
        if (breakEvenPercent < breakEvenWinRange[0] || breakEvenPercent > breakEvenWinRange[1]) return false;
      }

      // Search filter - most expensive, do last
      if (hasSearchFilter && searchTerms.length > 0) {
        const searchableText = `${bet.eventShortName || ''} ${bet.playerName || ''} ${bet.betTypeName || ''} ${bet.marketSourceName || ''}`.toLowerCase();
        
        for (const term of searchTerms) {
          if (!searchableText.includes(term)) {
            return false;
          }
        }
      }

      return true;
    });

    const filteredIds = filtered.map(b => b.id);
    logger.log('[FILTERED] Filtered bets recalculated. Count:', filtered.length, 'IDs:', filteredIds.slice(0, 10));
    
    return filtered;
  }, [processedBets, filterData, hideTracked, showHiddenBets, snipeMinEV, snipeMaxAge, dateRange, breakEvenWinRange, minGrowth]);

  // Clear expanded rows after filter changes AND use apiRef to force close detail panels
  useEffect(() => {
    if (expandedRowIds.size > 0) {
      logger.log('[CLEAR] Clearing expanded rows after filter change');
      
      // Use apiRef to forcefully close all detail panels
      if (apiRef.current?.setExpandedDetailPanels) {
        logger.log('[API] Calling apiRef.setExpandedDetailPanels(new Set())');
        apiRef.current.setExpandedDetailPanels(new Set());
      }
      
      setExpandedRowIds(new Set());
    }
  }, [leagueFilter, categoryFilter, betTypeFilter, bookFilter, searchQuery, dateRange, breakEvenWinRange, hideTracked, showHiddenBets, snipeMinEV, snipeMaxAge, minGrowth, apiRef]);

  // Remove the limitation - show all rows but blur the ones after the 3rd
  const displayBets = filteredBets;

  // Create a valid IDs set for current filtered data
  const validRowIdsFromFiltered = useMemo(() => {
    return new Set(filteredBets.map(bet => bet.id));
  }, [filteredBets]);

  // Derive the actual expanded IDs to use - filter out any that aren't in current dataset
  // This happens synchronously during render, BEFORE any effects
  const validExpandedRowIds = useMemo(() => {
    if (expandedRowIds.size === 0) return expandedRowIds;
    
    const valid = new Set([...expandedRowIds].filter(id => validRowIdsFromFiltered.has(id)));
    
    logger.log('[VALID-EXPANDED] Deriving validExpandedRowIds');
    logger.log('[VALID-EXPANDED] Raw expandedRowIds:', Array.from(expandedRowIds));
    logger.log('[VALID-EXPANDED] Valid IDs from filtered:', Array.from(validRowIdsFromFiltered).slice(0, 10));
    logger.log('[VALID-EXPANDED] Derived valid expanded IDs:', Array.from(valid));
    
    // If different, schedule cleanup for next render
    if (valid.size !== expandedRowIds.size) {
      logger.log('[VALID-EXPANDED] Scheduling cleanup of invalid IDs');
      Promise.resolve().then(() => {
        setExpandedRowIds(valid);
      });
    }
    
    return valid;
  }, [expandedRowIds, validRowIdsFromFiltered]);

  // CRITICAL: Clear expandedRowIds IMMEDIATELY when filtered data changes and invalidates expanded rows
  // This must run BEFORE any rendering to prevent DataGrid internal state issues
  useEffect(() => {
    if (expandedRowIds.size > 0) {
      const validIds = new Set(filteredBets.map(bet => bet.id));
      const hasInvalidIds = [...expandedRowIds].some(id => !validIds.has(id));
      
      if (hasInvalidIds) {
        logger.log('[SYNC-CLEAR] Immediately clearing invalid expandedRowIds');
        logger.log('[SYNC-CLEAR] Current expanded:', Array.from(expandedRowIds));
        logger.log('[SYNC-CLEAR] Valid IDs:', Array.from(validIds).slice(0, 10));
        
        // Clear immediately to prevent any rendering with invalid state
        setExpandedRowIds(new Set());
      }
    }
  }, [filteredBets]); // Only depend on filteredBets, not expandedRowIds

  useEffect(() => {
    logger.log('[VALID-EXPANDED-EFFECT] validExpandedRowIds changed:', Array.from(validExpandedRowIds));
  }, [validExpandedRowIds]);

  // Force DataGrid to remount when expanded rows are cleared
  const [gridKey, setGridKey] = useState(0);
  const prevValidExpandedSizeRef = useRef(0);
  
  useEffect(() => {
    const currentSize = validExpandedRowIds.size;
    const prevSize = prevValidExpandedSizeRef.current;
    
    // If we went from having expanded rows to having none, force a remount
    if (prevSize > 0 && currentSize === 0) {
      logger.log('[GRID-KEY] Forcing DataGrid remount: expanded rows cleared');
      logger.log('[GRID-KEY] Previous validExpandedRowIds size:', prevSize);
      logger.log('[GRID-KEY] Current validExpandedRowIds size:', currentSize);
      setGridKey(prev => prev + 1);
    }
    
    prevValidExpandedSizeRef.current = currentSize;
  }, [validExpandedRowIds]);

  // Handle detail panel expansion with validation

  // Combined calculation for best odds and associated bets maps
const { bestOddsMap, associatedBetsMap } = useMemo(() => {
  const bestOdds = new Map();
  const associatedBets = new Map();
  
  // Single pass through processedBets to build both maps
  processedBets.forEach((bet) => {
    if (!bet.betMarketId) return;
    
    // Build associated bets map
    if (!associatedBets.has(bet.betMarketId)) {
      associatedBets.set(bet.betMarketId, []);
    }
    associatedBets.get(bet.betMarketId).push(bet);
  });
  
  // Calculate best odds for each group - NO NEW OBJECTS
  associatedBets.forEach((group, betMarketId) => {
    const isOver = group[0]?.side === 'Over';
    
    // Sort using original objects - create shallow copy only for sorting
    const orderedGroup = [...group].sort((a, b) => {
      const lineA = Number(a.points) || 0;           // ✅ No new object
      const lineB = Number(b.points) || 0;
      const priceA = a.price ?? -Infinity;
      const priceB = b.price ?? -Infinity;

      if (lineA !== lineB) {
        return isOver ? lineA - lineB : lineB - lineA;
      }
      return priceB - priceA;
    });

    const bestBet = orderedGroup[0];
    if (bestBet) {
      bestOdds.set(betMarketId, {
        bestOdds: bestBet.price,
        bestLine: Number(bestBet.points) || 0,       // ✅ Compute inline
      });
    }
  });

  return { bestOddsMap: bestOdds, associatedBetsMap: associatedBets };
}, [processedBets]);

  // Modified getAssociatedBets to use the map
  const getAssociatedBets = useCallback((row) => {
    if (!row || !row.betMarketId) return [];
    return associatedBetsMap.get(row.betMarketId) || [];
  }, [associatedBetsMap]);

  // Initialize filters when options become available
  useEffect(() => {
    if (filterOptions.leagues.length > 0 && 
        filterOptions.categories.length > 0 && 
        filterOptions.betTypes.length > 0 && 
        filterOptions.books.length > 0 &&
        !leagueInitialized.current) {
      
      const savedLeague = JSON.parse(localStorage.getItem('leagueFilter') || '[]');
      const savedCategory = JSON.parse(localStorage.getItem('categoryFilter') || '[]');
      const savedBetType = JSON.parse(localStorage.getItem('betTypeFilter') || '[]');
      const savedBook = JSON.parse(localStorage.getItem('bookFilter') || '[]');
      
      // Only set to all options if no saved values exist
      if (savedLeague.length === 0 && savedCategory.length === 0 && 
          savedBetType.length === 0 && savedBook.length === 0) {
        setLeagueFilter(filterOptions.leagues);
        setCategoryFilter(filterOptions.categories);
        setBetTypeFilter(filterOptions.betTypes);
        
        // For books, exclude prophetX and novig by default
        const defaultBooks = filterOptions.books.filter(book => {
          const bookLower = book.toLowerCase();
          return bookLower !== 'prophetx' && bookLower !== 'novig';
        });
        setBookFilter(defaultBooks);
      } else {
        // Use saved values (already set from localStorage in state initialization)
        // This just marks them as initialized
      }
      
      leagueInitialized.current = true;
      categoryInitialized.current = true;
      betTypeInitialized.current = true;
      bookInitialized.current = true;
    }
  }, [filterOptions]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [leagueFilter, categoryFilter, betTypeFilter, bookFilter, hideTracked, showHiddenBets, snipeMinEV, snipeMaxAge, searchQuery, dateRange, breakEvenWinRange]);

  // Filter handlers with pre-emptive expandedRowIds clearing
  const handleFilterChange = useCallback((e, options, setter, debouncedSetter) => {
    const val = e.target.value;
    logger.log('[FILTER] Filter change detected. Value:', val);
    
    // CRITICAL: Clear the ref IMMEDIATELY (before anything else)
    expandedRowIdsRef.current = new Set();
    
    // Clear state
    setExpandedRowIds(new Set());
    
    // CRITICAL: Use apiRef to forcefully close all detail panels
    if (apiRef.current?.setExpandedDetailPanels) {
      logger.log('[FILTER] Calling apiRef.setExpandedDetailPanels(new Set())');
      try {
        apiRef.current.setExpandedDetailPanels(new Set());
      } catch (error) {
        logger.error('[FILTER] Error calling setExpandedDetailPanels:', error);
      }
    }
    
    if (val[val.length - 1] === 'all') {
      setter(prev => {
        const newValue = prev.length === options.length ? [] : options;
        logger.log('[FILTER] All toggle. New value length:', newValue.length);
        if (debouncedSetter) debouncedSetter(newValue);
        return newValue;
      });
    } else {
      logger.log('[FILTER] Setting filter to:', val.length, 'items');
      if (debouncedSetter) {
        debouncedSetter(val);
      } else {
        setter(val);
      }
    }
  }, [apiRef]);

  const renderFilterValue = (sel, opts, label) => {
    if (!sel.length) return label;
    if (sel.length === opts.length) return 'All';
    return sel.length > 1 ? `Multiple selections (${sel.length})` : sel[0];
  };

  // Export to CSV
  const exportToCSV = useCallback(() => {
    if (!filteredBets.length || !isAuthenticated) return;

    const headers = [
      'ID', 'League', 'Event', 'Event Start', 'Participant', 'Bet Type',
      'Book', 'Line', 'Odds', 'Implied%', 'Model%', 'Growth%', 'EV', 'Kelly', 'EV Age'
    ];

    const rows = filteredBets.map(bet => {
      const eventStart = bet.eventStart
        ? new Date(bet.eventStart).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }).replace(',', '')
        : '';
      const odds = bet.price == null ? '' : bet.price < 0 ? bet.price : `+${bet.price}`;
      const implied = bet.breakEvenWin == null ? '' : `${(bet.breakEvenWin * 100).toFixed(1)}%`;
      const model = bet.trueWin == null ? '' : `${(bet.trueWin * 100).toFixed(1)}%`;
      const growth = bet.growthRate == null ? '' : `${(bet.growthRate * 100).toFixed(2)}%`;
      const ev = bet.roi == null ? '' : `${(bet.roi * 100).toFixed(1)}%`;
      const kelly = bet.kelly == null ? '' : `$${(bet.kelly * bankroll * kellyFraction).toFixed(2)}`;
      const evAge = bet.lastROIChangeTime
        ? `${Math.floor((Date.now() - new Date(bet.lastROIChangeTime).getTime()) / 1000 / 60)} min`
        : '';

      return [
        bet.id, bet.leagueName || '', bet.eventShortName || '', eventStart,
        bet.playerName || '', bet.betTypeName || '', bet.marketSourceName || '',
        bet.sidepoints || '', odds, implied, model, growth, ev, kelly, evAge
      ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `bets_export_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredBets, bankroll, kellyFraction, isAuthenticated]);

  // Settings handlers
  const handleSettingsOpen = useCallback((event) => {
    setSettingsAnchorEl(event.currentTarget);
  }, []);

  const handleSettingsClose = useCallback(() => {
    setSettingsAnchorEl(null);
  }, []);

  const handleSliderChange = useCallback((event, newValue) => {
    const [min, max] = newValue;
    setBreakEvenWinRange([Math.min(min, max), Math.max(min, max)]);
  }, []);

  // Column renderers with proper memoization
  const eventCellRenderer = useCallback((params) => {
    const eventStart = params.row.eventStart
      ? new Date(params.row.eventStart).toLocaleString('en-US', {
          year: '2-digit',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }).replace(',', '')
      : '';
    const [awayTeam, homeTeam] = params.value.split('@');
    const leagueName = params.row.leagueName || '';
    
    return (
      <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
          {leagueName && (
            <>
              <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mr: 0 }}>
                {leagueName}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mx: 0.5 }}>
               |
              </Typography>
            </>
          )}
          {params.row.awayLogo && (
            <img
              src={params.row.awayLogo}
              alt={`${awayTeam} Logo`}
              style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
              loading="lazy"
            />
          )}
          <Typography sx={{ fontSize: '0.875rem' }}>{awayTeam}</Typography>
          <Typography sx={{ fontSize: '0.75rem', mx: '2px', paddingBottom: '2px' }}>@</Typography>
          {params.row.homeLogo && (
            <img
              src={params.row.homeLogo}
              alt={`${homeTeam} Logo`}
              style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
              loading="lazy"
            />
          )}
          <Typography sx={{ fontSize: '0.875rem' }}>{homeTeam}</Typography>
        </Box>
        <Typography sx={CELL_STYLES.secondaryText}>{eventStart}</Typography>
      </Box>
    );
  }, []);

    // Handle player name click to fetch game log
  const handlePlayerClick = useCallback(async (betRow) => {
    console.log('=== Player Click Debug ===');
    console.log('betRow:', betRow);
    console.log('LeagueId:', betRow.LeagueId);
    console.log('PersonId:', betRow.PersonId);
    
    // Try both uppercase and lowercase field names
    const leagueId = betRow.LeagueId || betRow.leagueId;
    const personId = betRow.PersonId || betRow.personId;
    
    console.log('Resolved leagueId:', leagueId);
    console.log('Resolved personId:', personId);
    
    // Check if this is an NFL bet (LeagueId === 1)
// Check if this is NFL (LeagueId === 1) or NBA (LeagueId === 3)
if (leagueId !== 1 && leagueId !== 3) {
  console.log('Not NFL or NBA, returning');
  return;
}

// Don't proceed if no PersonId
if (!personId) {
  console.log('No PersonId for this player');
  return;
}

// Determine league and sport
const league = leagueId === 3 ? 'nba' : 'nfl';
const sport = leagueId === 3 ? 'NBA' : 'NFL';
setGameLogSport(sport);

console.log('Fetching game log for personId:', personId);
setGameLogLoading(true);

try {
  const url = `https://ws.betpros.ai:8080/api/player-game-logs/${league}/${personId}`;
      console.log('Fetching from:', url);
      const response = await fetch(url);
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        console.error('Failed to fetch game log:', response.status);
        setGameLogLoading(false);
        return;
      }
      
      const data = await response.json();
      console.log('Received data:', data);
      console.log('gameLogs length:', data.gameLogs?.length);
      
      // Only open modal if we have game log data
       if (data && data.gameLogs && data.gameLogs.length > 0) {
        console.log('Opening modal with data');
        
        // Pass the API response data which has the correct structure
        setSelectedPlayerData({
          PlayerName: data.playerName,
          Position: data.position,
          Team: data.team,
          teamLogo: betRow.teamLogo,
          photoUrl: data.photoUrl,
          Height: data.Height,
          Weight: data.Weight,
          Age: data.Age,
          College: data.College,
          Experience: data.Experience,
          // Add upcoming game fields for the modal to use
          EventStart: betRow.eventStart,
          AwayShort: betRow.awayShort,
          HomeShort: betRow.homeShort,
          AwayLogo: betRow.awayLogo,
          HomeLogo: betRow.homeLogo
        });
        setGameLogData(data.gameLogs);
        setGameLogModalOpen(true);
      }else {
        console.log('No game log data available for this player');
      }
    } catch (error) {
      console.error('Error fetching game log:', error);
    } finally {
      setGameLogLoading(false);
      console.log('=== End Player Click Debug ===');
    }
  }, []);

  // Close modal handler
  const handleCloseModal = useCallback(() => {
    setGameLogModalOpen(false);
    setSelectedPlayerData(null);
    setGameLogData(null);
  }, []);

  const playerCellRenderer = useCallback((params) => {
    const playerName = params.row.playerName || '';
    const betTypeName = params.row.betTypeName || '';
    const isNFL = params.row.LeagueId === 1;
    const isNBA = params.row.LeagueId === 3;
    const hasPersonId = params.row.PersonId;
    const isClickable = (isNFL || isNBA) && hasPersonId;
    
    return (
      <Box sx={{ 
        textAlign: 'left', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        height: '100%', 
        width: '100%' 
      }}>
        <Typography
          onClick={isClickable ? (e) => {
            e.stopPropagation();
            handlePlayerClick(params.row);
          } : undefined}
          sx={{
            fontSize: '0.875rem',
            paddingLeft: '8px',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            width: '100%',
            cursor: isClickable ? 'pointer' : 'default',
            '&:hover': isClickable ? {
              textDecoration: 'underline'
            } : {}
          }}
        >
          {playerName}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.875rem',
            paddingLeft: '8px',
            color: '#BBBBBB',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {betTypeName}
        </Typography>
      </Box>
    );
  }, [handlePlayerClick]);

const logoCellRenderer = useCallback((p) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      width: '100%',
      height: '100%',
      paddingLeft: '8px',
    }}
  >
    <img
      src={bookmakerLogos[p.row.marketSourceName]}
      alt={p.row.marketSourceName}
      style={{ width: 72, height: 72, objectFit: 'contain' }}
      loading="lazy"
    />
  </div>
), []);

  const priceCellRenderer = useCallback((params) => {
    const value = params.value == null ? '' : params.value < 0 ? params.value : `+${params.value}`;
    const betMarketId = params.row.betMarketId;
    const isBestOdds = betMarketId && bestOddsMap.has(betMarketId) && 
      bestOddsMap.get(betMarketId).bestOdds === params.value && 
      bestOddsMap.get(betMarketId).bestLine === Number(params.row.points);
    const sidepoints = params.row.sidepoints || '';
    let prefix = sidepoints[0] || '';
    let numberStr = sidepoints.slice(1);
    let formattedNumber = '';
    let paddedPrefix = '      ';
  
    if (sidepoints && numberStr) {
      const numberParts = numberStr.split('.');
      const integerPart = numberParts[0] || '';
      const decimalPart = numberParts[1] || '0';
      if (integerPart) {
        formattedNumber = `${integerPart}.${decimalPart}`;
        const spaces = 6 - formattedNumber.length - prefix.length;
        paddedPrefix = ' '.repeat(spaces) + prefix;
      }
    }
  
    return (
      <Box
        sx={{
          ...CELL_STYLES.extendedBackground,
          backgroundColor: isBestOdds ? 'rgba(0, 255, 0, 0.2)' : 'transparent',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ color: '#ffffff', fontSize: '0.875rem', whiteSpace: 'pre' }}>{paddedPrefix}{formattedNumber}</Typography>
          <Typography sx={{ color: '#ffffff', fontSize: '0.875rem', ml: '4px' }}>{value}</Typography>
        </Box>
      </Box>
    );
  }, [bestOddsMap]);

  const roiCellRenderer = useCallback((p) => <RoiCell value={p.value} />, []);
  const growthCellRenderer = useCallback((p) => <GrowthRateCell value={p.value} />, []);
  
  const kellyCellRenderer = useCallback((p) => (
    <KellyCell value={p.value} bankroll={bankroll} kellyFraction={kellyFraction} />
  ), [bankroll, kellyFraction]);

  const evAgeCellRenderer = useCallback((params) => {
    const lastROIChangeTime = params.row.lastROIChangeTime;
    if (!lastROIChangeTime) return null;
    const ageSeconds = Math.max(0, (Date.now() - new Date(lastROIChangeTime).getTime()) / 1000);
    let displayValue = `${Math.floor(ageSeconds / 60).toString().padStart(3, ' ')} min`;
    return (
      <RoiAgeCell
        ageSeconds={ageSeconds}
        displayValue={displayValue}
        snipeMaxAge={snipeMaxAge}
      />
    );
  }, [snipeMaxAge]);

  const trackCellRenderer = useCallback((p) => (
    <TrackBetCell
      row={p.row}
      trackedBets={trackedBets}
      toggleTrack={debouncedToggleTrack}
      trackBet={safeguardedTrackBet}
      untrackBet={safeguardedUntrackBet}
      trackingInProgress={trackingInProgress}
      immediatelyTracked={immediatelyTracked || new Set()}
      isAuthenticated={isAuthenticated}
    />
  ), [trackedBets, debouncedToggleTrack, safeguardedTrackBet, safeguardedUntrackBet, trackingInProgress, immediatelyTracked, isAuthenticated]);

const hideCellRenderer = useCallback((p) => (
  <HideBetCell
    row={p.row}
    hiddenBets={hiddenBets}
    setHiddenBets={setHiddenBets}
    toggleHide={toggleHide}
    showHiddenBets={showHiddenBets}
    isAuthenticated={isAuthenticated}
    hideMarket={hideMarket}
    processedBets={processedBets}
  />
), [hiddenBets, setHiddenBets, toggleHide, showHiddenBets, isAuthenticated, hideMarket, processedBets]);

const deeplinkCellRenderer = useCallback((p) => (
  <DeeplinkCell 
    row={p.row} 
    isAuthenticated={isAuthenticated} 
    fetchDeeplink={fetchDeeplink}
  />
), [isAuthenticated, fetchDeeplink]);

  // Map of field to renderer
  const columnRenderers = useMemo(() => ({
    eventShortName: eventCellRenderer,
    playerName: playerCellRenderer,
    logourl: logoCellRenderer,
    price: priceCellRenderer,
    roi: roiCellRenderer,
    growthRate: growthCellRenderer,
    lastROIChangeTime: evAgeCellRenderer,
    kelly: kellyCellRenderer,
    trackBet: trackCellRenderer,
    hideBet: hideCellRenderer,
    deeplink: deeplinkCellRenderer,
  }), [eventCellRenderer, playerCellRenderer, logoCellRenderer, priceCellRenderer, roiCellRenderer, growthCellRenderer, evAgeCellRenderer, kellyCellRenderer, trackCellRenderer, hideCellRenderer, deeplinkCellRenderer]);

  // Desktop column definitions
  const columns = useMemo(() => {
    return STATIC_COLUMN_DEFINITIONS.map(col => {
      const column = { ...col };
      
      // Add render headers
      switch (col.field) {
        case 'price':
          column.renderHeader = HEADER_RENDERERS.withTooltip(col.headerName, 'Highlighted if best odds for the market.');
          break;
        case 'growthRate':
          column.renderHeader = HEADER_RENDERERS.withTooltip(
            col.headerName,
            'If you were to place this bet over and over at the recommended Kelly size, this is the average % that your bankroll will grow for each bet. This is essentially the EV for your entire bankroll as opposed to the individual bet.'
          );
          break;
        case 'lastROIChangeTime':
          column.valueGetter = params => {
            if (!params.value) return null;
            const lastChangeTime = new Date(params.value).getTime();
            return Math.max(0, (Date.now() - lastChangeTime) / 1000);
          };
          // Fall through to default header
        default:
          if (col.headerName) {
            column.renderHeader = HEADER_RENDERERS.default(col.headerName);
          }
          break;
      }
      
      // Add render cell from map
      if (columnRenderers[col.field]) {
        column.renderCell = columnRenderers[col.field];
      }
      
      return column;
    });
  }, [columnRenderers]);

  // Detail panel for desktop
    const DetailPanelItem = React.memo(({ index, style, data }) => {
    const bet = data.bets[index];
    return (
      <Box
        sx={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          p: '0 11px',
          border: 'none',
          borderRadius: '4px',
          width: '100%',
        }}
      >
        <Box sx={{ mr: 0.5, transform: 'translateY(3px)' }}>
          {bookmakerLogos[bet.marketSourceName] ? (
            <img
              src={bookmakerLogos[bet.marketSourceName]}
              alt={bet.marketSourceName}
              style={{ width: 44, height: 44, objectFit: 'contain' }}
              loading="lazy"
            />
          ) : (
            <Typography sx={{ fontSize: '0.875rem', color: '#ffffff' }}>No Logo</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
          <Typography
            variant="body2"
            sx={{
              color: '#ffffff',
              mr: 0.5,
              whiteSpace: 'nowrap',
              lineHeight: '44px',
              fontSize: '0.875rem',
            }}
          >
            {bet.sidepoints || ' '}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#ffffff',
              whiteSpace: 'nowrap',
              lineHeight: '44px',
              fontSize: '0.875rem',
            }}
          >
            {bet.price == null ? '' : bet.price < 0 ? bet.price : `+${bet.price}`}
          </Typography>
        </Box>
      </Box>
    );
  });

  // Modified getDetailPanelContent to use the map
  const getDetailPanelContent = useCallback(({ row }) => {
    
    // CRITICAL: If filters just changed, don't render any detail panels
    if (filtersChangedThisRender.current) {
      return null;
    }
    
    // CRITICAL: Verify row exists in current filtered dataset FIRST
    // This is the real safety check - if row isn't in filtered data, don't render
    if (!row || !row.id || !validRowIdsFromFiltered.has(row.id)) {
      return null;
    }
    
    if (!row.betMarketId) {
      return null;
    }
    
    // Get associated bets from the map
    const associatedBets = associatedBetsMap.get(row.betMarketId) || [];
    
    if (associatedBets.length <= 1) {
      // No associated bets - just return null without updating state
      return null;
    }
    
    // Rest of the rendering logic...
try {

  const isOver = associatedBets[0]?.side === 'Over';  // Check directly, no parsed copy needed
  
  // Create shallow copy ONLY for sorting (doesn't clone objects, just array)
  const orderedBets = [...associatedBets].sort((a, b) => {
    // Compute these inline during comparison - no need to store
    const lineA = Number(a.points) || 0;
    const lineB = Number(b.points) || 0;
    const priceA = a.price ?? -Infinity;
    const priceB = b.price ?? -Infinity;

    if (lineA !== lineB) {
      return isOver ? lineA - lineB : lineB - lineA;
    }
    return priceB - priceA;
  });
  
      const displayedBets = orderedBets.slice(0, 15);
      const mainRowIndex = displayedBets.findIndex(bet => bet && bet.id === row.id);
  
      return (
        <Box
          sx={{
            p: '2px 0',
            backgroundColor: '#2a2a2a',
            borderRadius: '4px',
            overflowX: 'auto',
            overflowY: 'hidden',
            display: 'flex',
            flexWrap: 'nowrap',
            height: 52,
            '&::-webkit-scrollbar': {
              height: '7px',
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#333333',
              border: 'none',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888888',
              border: 'none',
              '&:hover': {
                backgroundColor: '#aaaaaa',
              },
            },
          }}
        >
          {displayedBets.map((bet, index) => {
            // Additional safety check for each bet
            if (!bet || !bet.id) {
              return null;
            }
            
            return (
              <Box
                key={bet.id || index}
                sx={{
                  width: 150,
                  flexShrink: 0,
                  flexGrow: 0,
                  display: 'flex',
                  alignItems: 'center',
                  borderRight: '1px solid #ffffff',
                  pr: '11px',
                  backgroundColor: index === mainRowIndex ? 'rgba(0, 150, 255, 0.2)' : '#2a2a2a',
                }}
              >
                <DetailPanelItem
                  index={index}
                  data={{ bets: displayedBets }}
                />
              </Box>
            );
          })}
        </Box>
      );
    } catch (error) {
      logger.error('Error in getDetailPanelContent:', error, 'Row:', row);
      return null;
    }
  }, [associatedBetsMap, validRowIdsFromFiltered, detailPanelsDisabled]);
  const validRowIdsSet = useMemo(() => {
  return new Set(displayBets.map(bet => bet?.id).filter(Boolean));
}, [displayBets]);
  // Modified getDetailPanelHeight to use the map
  const getDetailPanelHeight = useCallback((params) => {
    try {
      const rowId = params?.row?.id;
      
      // CRITICAL: If filters just changed, return 0 immediately
      if (filtersChangedThisRender.current) {
        return 0;
      }
      
      // If detail panels are disabled, return 0 immediately
      if (detailPanelsDisabled) {
        return 0;
      }
      
      // Add more defensive checks
      if (!params || typeof params !== 'object') {
        return 0;
      }
      
      if (!params.row || typeof params.row !== 'object') {
        return 0;
      }
      
      if (!rowId) {
        return 0;
      }
      
     // CRITICAL: Verify row exists in current filtered dataset FIRST
     if (!validRowIdsFromFiltered.has(rowId)) {
        return 0;
     }
     
     // Also check validRowIdsSet (displayBets)
      if (!validRowIdsSet.has(rowId)) {
        return 0;
     }
     
      // Check if row has a valid betMarketId
      const betMarketId = params.row?.betMarketId;
      if (!betMarketId) {
        return 0;
      }
      
      // Get associated bets from the map
      const associatedBets = associatedBetsMap.get(betMarketId) || [];
      
      // Only show detail panel if there are multiple associated bets
      if (associatedBets.length <= 1) {
        return 0;
      }
      
      // Return the fixed height that matches getDetailPanelContent
      return 52;
    } catch (error) {
      return 0;
    }
 }, [validExpandedRowIds, associatedBetsMap, validRowIdsSet, validRowIdsFromFiltered, detailPanelsDisabled]);

  const safeGetDetailPanelHeight = useCallback((params) => {
    try {
      if (!params || !params.row || !params.row.id) {
        return 0;
      }
      return getDetailPanelHeight(params);
    } catch (error) {
      return 0;
    }
  }, [getDetailPanelHeight]);
  
  // Handle detail panel expansion with validation
  const handleDetailPanelExpanded = useCallback((newExpandedRowIds) => {
    try {
      let expandedArray;
      
      if (Array.isArray(newExpandedRowIds)) {
        expandedArray = newExpandedRowIds;
      } else if (newExpandedRowIds && typeof newExpandedRowIds[Symbol.iterator] === 'function') {
        expandedArray = Array.from(newExpandedRowIds);
      } else {
        expandedArray = [];
      }
      
      // Create a Set of currently valid row IDs from displayBets
      const validRowIds = new Set(displayBets.map(row => row?.id).filter(Boolean));
      
      // Filter out invalid rows and rows without associated bets
      const filteredExpandedRowIds = expandedArray.filter(id => {
        if (!id || !validRowIds.has(id)) return false;
        
        // Check if this row has associated bets
        const row = displayBets.find(bet => bet.id === id);
        if (!row || !row.betMarketId) return false;
        
        const associatedBets = associatedBetsMap.get(row.betMarketId) || [];
        return associatedBets.length > 1;
      });
      
      setExpandedRowIds(new Set(filteredExpandedRowIds));
    } catch (error) {
      logger.error('Error in handleDetailPanelExpanded:', error);
      setExpandedRowIds(new Set());
    }
  }, [displayBets, associatedBetsMap]);

  
  // Mobile page setup
  const mobilePageSize = 10;
const paginatedBets = useMemo(() => {
  const startIdx = currentPage * mobilePageSize;
  const endIdx = startIdx + mobilePageSize;
  
  return [...displayBets]
    .sort((a, b) => {
      const aGrowth = a.growthRate || 0;
      const bGrowth = b.growthRate || 0;
      return bGrowth - aGrowth;
    })
    .slice(startIdx, endIdx);
}, [displayBets, currentPage]);
  const totalPages = Math.ceil(displayBets.length / mobilePageSize);

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ 
          width: '100%', 
          height: '100vh', 
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
{/* Mobile View - Only render on small screens */}
{screenWidth < 900 && (
  <>
    {/* Mobile Header */}
    <Box sx={{ 
      position: 'sticky',
      top: '-8px',
      backgroundColor: '#121212',
      px: 1.5,
      pt: 0,
      pb: 0.75,
      mt: 0,
      borderBottom: '1px solid #333',
      zIndex: 10,
      minHeight: 95,
      '& > *:first-of-type': {
        mt: 0,
      }
    }}>
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="flex-start" 
        spacing={1}
        sx={{ 
          height: '100%',
          mt: 0,
          pt: 0.75,
        }}
      >
        <Stack 
          direction="column" 
          spacing={1.25}
          sx={{ 
            flex: 1, 
            minWidth: 0
          }}
        >
          {/* First row: League and Category */}
          <Stack direction="row" spacing={.5}>
            <FormControl 
              variant="outlined" 
              size="small" 
              sx={{ 
                flex: 1, 
                minWidth: 100,
                maxWidth: 140
              }}
            >
              <InputLabel 
                id="mobile-league-filter-label" 
                shrink 
                sx={{ 
                  color: '#ffffff', 
                  fontSize: '0.875rem'
                }}
              >
                League
              </InputLabel>
              <Select
                multiple
                labelId="mobile-league-filter-label"
                label="League"
                notched
                value={leagueFilter}
                onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter)}
                renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
                disabled={!isAuthenticated}
                sx={{ 
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    paddingRight: '32px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: '#ffffff'
                  },
                  '&.Mui-disabled': {
                    '& .MuiSelect-select': { color: '#ffffff', WebkitTextFillColor: '#ffffff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
                  },
                }}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 300 } },
                  disableAutoFocusItem: true,
                  autoFocus: false,
                }}
              >
                <MenuItem value="all">
                  <Checkbox checked={leagueFilter.length === filterOptions.leagues.length} />
                  <ListItemText primary="Select All" />
                </MenuItem>
                {filterOptions.leagues.map(o => (
                  <MenuItem key={o} value={o}>
                    <Checkbox checked={leagueFilter.includes(o)} />
                    <ListItemText primary={o} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl 
              variant="outlined" 
              size="small" 
              sx={{ 
                flex: 1, 
                minWidth: 100,
                maxWidth: 140
              }}
            >
              <InputLabel 
                id="mobile-category-filter-label" 
                shrink 
                sx={{ 
                  color: '#ffffff', 
                  fontSize: '0.875rem'
                }}
              >
                Category
              </InputLabel>
              <Select
                multiple
                labelId="mobile-category-filter-label"
                label="Category"
                notched
                value={categoryFilter}
                onChange={e => handleFilterChange(e, filterOptions.categories, setCategoryFilter)}
                renderValue={sel => renderFilterValue(sel, filterOptions.categories, 'Category')}
                disabled={!isAuthenticated}
                sx={{ 
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    paddingRight: '32px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: '#ffffff'
                  },
                  '&.Mui-disabled': {
                    '& .MuiSelect-select': { color: '#ffffff', WebkitTextFillColor: '#ffffff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
                  },
                }}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 300 } },
                  disableAutoFocusItem: true,
                  autoFocus: false,
                }}
              >
                <MenuItem value="all">
                  <Checkbox checked={categoryFilter.length === filterOptions.categories.length} />
                  <ListItemText primary="Select All" />
                </MenuItem>
                {filterOptions.categories.map(o => (
                  <MenuItem key={o} value={o}>
                    <Checkbox checked={categoryFilter.includes(o)} />
                    <ListItemText primary={o} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Second row: Book and Bet Type */}
          <Stack direction="row" spacing={.5}>
            <FormControl 
              variant="outlined" 
              size="small" 
              sx={{ 
                flex: 1, 
                minWidth: 100,
                maxWidth: 140
              }}
            >
              <InputLabel 
                id="mobile-book-filter-label" 
                shrink 
                sx={{ 
                  color: '#ffffff', 
                  fontSize: '0.875rem'
                }}
              >
                Book
              </InputLabel>
              <Select
                multiple
                labelId="mobile-book-filter-label"
                label="Book"
                notched
                value={bookFilter}
                onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter)}
                renderValue={sel => renderFilterValue(sel, filterOptions.books, 'Book')}
                disabled={!isAuthenticated}
                sx={{ 
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    paddingRight: '32px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: '#ffffff'
                  },
                  '&.Mui-disabled': {
                    '& .MuiSelect-select': { color: '#ffffff', WebkitTextFillColor: '#ffffff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
                  },
                }}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 300 } },
                  disableAutoFocusItem: true,
                  autoFocus: false,
                }}
              >
                <MenuItem value="all">
                  <Checkbox checked={bookFilter.length === filterOptions.books.length} />
                  <ListItemText primary="Select All" />
                </MenuItem>
                {filterOptions.books.map(o => (
                  <MenuItem key={o} value={o}>
                    <Checkbox checked={bookFilter.includes(o)} />
                    <ListItemText primary={o} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl 
              variant="outlined" 
              size="small" 
              sx={{ 
                flex: 1, 
                minWidth: 100,
                maxWidth: 140
              }}
            >
              <InputLabel 
                id="mobile-bettype-filter-label" 
                shrink 
                sx={{ 
                  color: '#ffffff', 
                  fontSize: '0.875rem'
                }}
              >
                Bet Type
              </InputLabel>
              <Select
                multiple
                labelId="mobile-bettype-filter-label"
                label="Bet Type"
                notched
                value={betTypeFilter}
                onChange={e => handleFilterChange(e, filterOptions.betTypes, setBetTypeFilter)}
                renderValue={sel => renderFilterValue(sel, filterOptions.betTypes, 'Bet Type')}
                disabled={!isAuthenticated}
                sx={{ 
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    paddingRight: '32px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: '#ffffff'
                  },
                  '&.Mui-disabled': {
                    '& .MuiSelect-select': { color: '#ffffff', WebkitTextFillColor: '#ffffff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
                  },
                }}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 300 } },
                  disableAutoFocusItem: true,
                  autoFocus: false,
                }}
              >
                <MenuItem value="all">
                  <Checkbox checked={betTypeFilter.length === filterOptions.betTypes.length} />
                  <ListItemText primary="Select All" />
                </MenuItem>
                {filterOptions.betTypes.map(o => (
                  <MenuItem key={o} value={o}>
                    <Checkbox checked={betTypeFilter.includes(o)} />
                    <ListItemText primary={o} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
        
        <IconButton 
          onClick={handleSettingsOpen} 
          disabled={!isAuthenticated}
          sx={{ 
            color: '#ffffff', 
            flexShrink: 0,
            '&.Mui-disabled': { 
              color: '#ffffff',
              opacity: 0.5,
            },
          }}
        >
          <SettingsIcon sx={{ fontSize: '1.2rem' }} />
        </IconButton>
      </Stack>
    </Box>

    {/* Mobile Content */}
    <Box sx={{ 
      flex: 1,
      overflowY: 'auto',
      p: 1.5,
      paddingBottom: totalPages > 1 ? '80px' : '16px',
      position: 'relative',
    }}>
      {/* Mobile blur overlay for unauthenticated users */}
      {!isAuthenticated && paginatedBets.length > 0 && (
        <>
          {/* Gradient overlay starting after the first card */}
          <Box
            sx={{
              position: 'absolute',
              top: '220px',
              left: 0,
              right: 0,
              height: '60px',
              background: 'linear-gradient(to bottom, transparent, rgba(18, 18, 18, 0.8))',
              zIndex: 999,
              pointerEvents: 'none',
            }}
          />
          {/* Blur overlay covering cards after the first */}
          <Box
            sx={{
              position: 'absolute',
              top: '280px',
              left: 0,
              right: 0,
              bottom: totalPages > 1 ? '64px' : 0,
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              backgroundColor: 'rgba(18, 18, 18, 0.3)',
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          />
          {/* Login prompt within the blurred section */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1001,
              width: '90%',
              maxWidth: 350,
            }}
          >
            <Box
              sx={{
                backgroundColor: '#1e1e1e',
                border: '2px solid #ff1744',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
              }}
            >
              <Typography variant="body2" sx={{ color: '#BBBBBB', mb: 3 }}>
              Please login or subscribe to access all SharpStack features
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: '#ff1744',
                    color: '#ffffff',
                    '&:hover': { backgroundColor: '#d50000' },
                  }}
                  onClick={() => window.top.location.href = 'https://www.4for4.com/user/login?destination=sharpstack'}
                >
                  Log In
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: '#ff1744',
                    color: '#ffffff',
                    '&:hover': { backgroundColor: '#d50000' },
                  }}
                  onClick={() => window.top.location.href = 'https://www.4for4.com/plans'}
                >
                  Subscribe
                </Button>
              </Stack>
            </Box>
          </Box>
        </>
      )}
      {/* Render only the first card if not authenticated */}
      {paginatedBets.slice(0, isAuthenticated ? undefined : 1).map((bet, index) => {
        const isBestOdds = bet.betMarketId && bestOddsMap.has(bet.betMarketId) && 
          bestOddsMap.get(bet.betMarketId).bestOdds === bet.price && 
          bestOddsMap.get(bet.betMarketId).bestLine === Number(bet.points);
        
        return (
          <MobileBetCard
            key={bet.id}
            bet={bet}
            bankroll={bankroll}
            kellyFraction={kellyFraction}
            trackedBets={trackedBets}
            hiddenSet={filterData.hiddenSet}
            hiddenMarketSet={filterData.hiddenMarketSet}
            showHiddenBets={showHiddenBets}
            toggleTrack={debouncedToggleTrack}
            toggleHide={toggleHide}
            trackBet={safeguardedTrackBet}
            untrackBet={safeguardedUntrackBet}
            trackingInProgress={trackingInProgress}
            associatedBets={getAssociatedBets(bet)}
            isBestOdds={isBestOdds}
            immediatelyTracked={immediatelyTracked || new Set()}
            isAuthenticated={isAuthenticated}
            fetchDeeplink={fetchDeeplink}
          />
        );
      })}
    </Box>

    {/* Mobile Pagination */}
    {totalPages > 1 && (
      <Box sx={{ 
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#1e1e1e',
        borderTop: '1px solid #333',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.5)',
        p: 2,
        zIndex: 1002,
        flexShrink: 0,
        opacity: isAuthenticated ? 1 : 0.5,
        pointerEvents: isAuthenticated ? 'auto' : 'none',
      }}>
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
          <Button
            size="small"
            onClick={() => isAuthenticated && setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0 || !isAuthenticated}
            sx={{ 
              color: '#ffffff',
              '&.Mui-disabled': { 
                color: '#666666' 
              }
            }}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ color: isAuthenticated ? '#ffffff' : '#666666' }}>
            {currentPage + 1} / {totalPages}
          </Typography>
          <Button
            size="small"
            onClick={() => isAuthenticated && setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
            disabled={currentPage >= totalPages - 1 || !isAuthenticated}
            sx={{ 
              color: '#ffffff',
              '&.Mui-disabled': { 
                color: '#666666' 
              }
            }}
          >
            Next
          </Button>
        </Stack>
      </Box>
    )}
  </>
)}

{/* Desktop View - Only render on large screens */}
{screenWidth >= 900 && (
  <Box sx={{ 
    width: '100%', 
    height: '100vh', 
    backgroundColor: 'background.default', 
    flexDirection: 'column', 
    overflow: 'hidden',
    position: 'relative',
  }}>
    {/* Filter Section */}
    <Box className="filter-section" sx={{ flex: 'none', boxSizing: 'border-box' }} ref={filterRef}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2}>
          {[
            { label: 'League', value: leagueFilter, set: setLeagueFilter, opts: filterOptions.leagues, id: 'league' },
            { label: 'Category', value: categoryFilter, set: setCategoryFilter, opts: filterOptions.categories, id: 'category' },
            { label: 'Bet Type', value: betTypeFilter, set: setBetTypeFilter, opts: filterOptions.betTypes, id: 'bet-type' },
            { label: 'Book', value: bookFilter, set: setBookFilter, opts: filterOptions.books, id: 'book' },
          ].map(({ label, value, set, opts, id }) => (
            <FormControl key={id} variant="outlined" size="small" sx={{ minWidth: 120 }}>
              <InputLabel id={`${id}-filter-label`} shrink sx={{ color: '#ffffff', fontSize: '0.875rem' }}>{label}</InputLabel>
              <Select
                multiple
                labelId={`${id}-filter-label`}
                label={label}
                notched
                value={value}
                onChange={e => handleFilterChange(e, opts, set)}
                renderValue={sel => renderFilterValue(sel, opts, label)}
                disabled={!isAuthenticated}
                sx={{ 
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': { color: '#ffffff' },
                  '&.Mui-disabled': {
                    '& .MuiSelect-select': { color: '#ffffff', WebkitTextFillColor: '#ffffff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
                  },
                }}
              >
                <MenuItem value="all">
                  <Checkbox checked={value.length === opts.length} />
                  <ListItemText primary="Select All" sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                </MenuItem>
                {opts.map(o => (
                  <MenuItem key={o} value={o}>
                    <Checkbox checked={value.includes(o)} />
                    <ListItemText primary={o} sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
          <TextField
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="e.g. jokic mgm points"
            size="small"
            disabled={!isAuthenticated}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#BBBBBB' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 225,
              '& .MuiInputBase-input': { 
                color: '#ffffff' 
              },
              '& .MuiInputBase-input::placeholder': { 
                color: '#BBBBBB', 
                opacity: 1 
              },
              '& .MuiInputLabel-root': { 
                fontSize: '0.875rem' 
              },
              // Override all disabled opacity
              '&.MuiTextField-root.Mui-disabled': {
                '& *': {
                  opacity: '1 !important',
                  '-webkit-text-fill-color': 'unset !important'
                },
                '& .MuiInputBase-input::placeholder': { 
                  color: '#BBBBBB !important', 
                  opacity: '1 !important',
                  '-webkit-text-fill-color': '#BBBBBB !important'
                },
                '& .MuiOutlinedInput-notchedOutline': { 
                  borderColor: '#ffffff' 
                }
              }
            }}
          />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={handleSettingsOpen} disabled={!isAuthenticated} sx={{ color: '#ffffff', '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 } }}>
            <SettingsIcon sx={{ fontSize: '1.2rem' }} />
          </IconButton>
          <IconButton
            onClick={exportToCSV}
            disabled={filteredBets.length === 0 || !isAuthenticated}
            sx={{
              color: '#ffffff',
              '&:hover': { backgroundColor: 'rgba(187, 187, 187, 0.1)' },
              '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 },
            }}
          >
            <DownloadIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Box>

    {/* Table Section */}
    <Box className="table-section">
      <Box className="data-grid-container" ref={gridContainerRef} sx={{ height: '100%', boxSizing: 'border-box' }}>
        {/* Debug logging */}
        {(() => {
          const expandedIds = detailPanelsDisabled ? new Set() : validExpandedRowIds;
          return null;
        })()}
        <DataGridPro
          key={gridKey}
          apiRef={apiRef}
          rows={displayBets}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          paginationMode="client"
          page={currentPage}
          onPageChange={setCurrentPage}
          sortingMode="client"
          sortModel={sortModel}
          onSortModelChange={newModel => setSortModel(newModel)}
          disableSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          loading={false}
          getRowId={row => row.id}
          getRowClassName={(params) => params.row.isDetail ? 'detail-row' : ''}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={safeGetDetailPanelHeight}
          hideFooterSelectedRowCount
          sx={{
            '& .MuiDataGrid-columnHeaders': { 
              backgroundColor: '#1e1e1e', 
              borderBottom: '2px solid #444',
              position: 'sticky',
              top: '56px',
              zIndex: 2,
            },
            '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
            '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
            '& .MuiDataGrid-cell': {
              color: '#ffffff !important',
              userSelect: 'none',
              fontSize: '0.875rem',
              borderRight: '1px solid rgba(255, 255, 255, 0.3)',
            },
            '& .MuiDataGrid-cell:last-child': {
              borderRight: 'none',
            },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
            '& .MuiDataGrid-columnSeparator': { 
              visibility: 'visible',
              color: 'rgba(255, 255, 255, 0.3)',
            },
            '& .MuiDataGrid-iconButtonContainer': { 
              color: '#ffffff !important', 
              padding: '0px',
              backgroundColor: 'transparent !important',
              '&:hover': { backgroundColor: 'transparent !important' },
            },
            '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
            '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { 
              color: '#ffffff !important',
              fontSize: '0.875rem',
            },
            '& .MuiSelect-icon': { color: '#ffffff !important' },
            '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
            '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
            '& .MuiDataGrid-main': {
              padding: 0,
              boxSizing: 'border-box',
              overflow: 'hidden',
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: '#1e1e1e',
              color: '#ffffff !important',
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
              boxSizing: 'border-box',
            },
            ...(!isAuthenticated && {
              '& .MuiDataGrid-virtualScroller': {
                overflowY: 'hidden !important',
                overflowX: 'hidden !important',
                pointerEvents: 'none',
                height: '156px',
                '&::-webkit-scrollbar': {
                  display: 'none',
                  width: 0,
                  height: 0,
                },
              },
              '& .MuiDataGrid-virtualScrollerContent': {
                overflow: 'hidden !important',
                pointerEvents: 'none',
                height: '156px',
              },
              '& .MuiDataGrid-scrollbar': {
                display: 'none !important',
              },
            }),
          }}
          rowBuffer={10}
          rowThreshold={5}
        />
      </Box>
      {/* Desktop blur overlay for unauthenticated users */}
      {!isAuthenticated && filteredBets.length > 3 && (
        <>
          {/* Blur effect applied directly after the 3rd row */}
          <Box
            sx={{
              position: 'absolute',
              top: '212px',
              left: 0,
              right: 0,
              bottom: 0,
              backdropFilter: 'blur(5px)',
              WebkitBackdropFilter: 'blur(5px)',
              backgroundColor: 'rgba(18, 18, 18, 0.35)',
              zIndex: 11,
              pointerEvents: 'none',
            }}
          />
          {/* Login prompt overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 12,
            }}
          >
            <Box
              sx={{
                backgroundColor: '#1e1e1e',
                border: '2px solid #ff1744',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
              }}
            >
              <Typography variant="body1" sx={{ color: '#BBBBBB', mb: 3 }}>
              Please login or subscribe to access all SharpStack features
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#ff1744',
                    color: '#ffffff',
                    '&:hover': { backgroundColor: '#d50000' },
                  }}
                  onClick={() => window.top.location.href = 'https://www.4for4.com/user/login?destination=sharpstack'}
                >
                  Log In
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#ff1744',
                    color: '#ffffff',
                    '&:hover': { backgroundColor: '#d50000' },
                  }}
                  onClick={() => window.top.location.href = 'https://www.4for4.com/plans'}
                >
                  Subscribe
                </Button>
              </Stack>
            </Box>
          </Box>
        </>
      )}
    </Box>
  </Box>
)}

          {/* Settings Popover */}
          <Popover
            open={Boolean(settingsAnchorEl)}
            anchorEl={settingsAnchorEl}
            onClose={handleSettingsClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Box sx={{ p: '12px 16px', width: 320, backgroundColor: '#1e1e1e', color: '#ffffff' }}>
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth size="small" margin="dense">
                  <InputLabel id="date-range-label" sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Date Range</InputLabel>
                  <Select
                    labelId="date-range-label"
                    label="Date Range"
                    value={dateRange}
                    onChange={e => setDateRange(e.target.value === 'null' ? null : Number(e.target.value))}
                    sx={{ 
                      fontSize: '0.875rem', 
                      '& .MuiSelect-select': { color: 'white' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
                    }}
                  >
                    <MenuItem value={1}>1 Day</MenuItem>
                    <MenuItem value={2}>2 Days</MenuItem>
                    <MenuItem value={7}>7 Days</MenuItem>
                    <MenuItem value={14}>14 Days</MenuItem>
                    <MenuItem value="null">Full Range</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  notched
                  label="Min EV (%)"
                  type="text"
                  value={minEVDisplay}
                  onChange={e => {
                    const val = e.target.value;
                    setMinEVDisplay(val);  // Always update display as user types
                    // Only update the numeric value if it's empty or a valid complete number
                    if (val === '') {
                      setSnipeMinEV(null);
                    } else if (val !== '.' && val !== '.0' ) {
                      const num = parseFloat(val);
                      if (!isNaN(num)) {
                        setSnipeMinEV(num);
                      }
                    }
                    // Don't update minEV for partial inputs like "." or "0." or ".0"
                  }}
                  onFocus={e => {
                    if (e.target.value === 'No Min') {
                      setMinEVDisplay('');
                    }
                  }}
                  onBlur={e => {
                    const val = e.target.value;
                    if (val === '' || isNaN(parseFloat(val))) {
                      setSnipeMinEV(null);
                      setMinEVDisplay('No Min');
                    } else {
                      const num = parseFloat(val);
                      setSnipeMinEV(num);
                      setMinEVDisplay(num.toString());
                    }
                  }}
                  fullWidth
                  size="small"
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  sx={{ 
                    '& .MuiInputBase-input': { color: 'white' }, 
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
                  }}
                />
                <TextField
                  notched
                  label="Min Growth (%)"
                  type="text"
                  value={minGrowthDisplay}
                  onChange={e => {
                    const val = e.target.value;
                    setMinGrowthDisplay(val);
                    if (val === '') {
                      setMinGrowth(null);
                    } else  if (val !== '.' && val !== '.0' ) {
                      const num = parseFloat(val);
                      setMinGrowth(isNaN(num) ? null : num);
                    }
                  }}
                  onFocus={e => {
                    if (e.target.value === 'No Min') {
                      setMinGrowthDisplay('');
                    }
                  }}
                  onBlur={e => {
                    const val = e.target.value;
                    if (val === '' || isNaN(parseFloat(val))) {
                      setMinGrowth(null);
                      setMinGrowthDisplay('No Min');
                    } else {
                      const num = parseFloat(val);
                      setMinGrowth(num);
                      setMinGrowthDisplay(num.toString());
                    }
                  }}
                  fullWidth
                  size="small"
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  sx={{ 
                    '& .MuiInputBase-input': { color: 'white' }, 
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
                  }}
                />
                <TextField
                  notched
                  label="Max EV Age (min)"
                  type="text"
                  value={maxAgeDisplay}
                  onChange={e => {
                    const val = e.target.value;
                    setMaxAgeDisplay(val);
                    if (val === '') {
                      setSnipeMaxAge(null);
                    } else {
                      const num = parseInt(val);
                      setSnipeMaxAge(isNaN(num) || num < 0 ? null : num);
                    }
                  }}
                  onFocus={e => {
                    if (e.target.value === 'No Max') {
                      setMaxAgeDisplay('');
                    }
                  }}
                  onBlur={e => {
                    const val = e.target.value;
                    if (val === '' || isNaN(parseInt(val)) || parseInt(val) < 0) {
                      setSnipeMaxAge(null);
                      setMaxAgeDisplay('No Max');
                    } else {
                      const num = parseInt(val);
                      setSnipeMaxAge(num);
                      setMaxAgeDisplay(num.toString());
                    }
                  }}
                  fullWidth
                  size="small"
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  sx={{ 
                    '& .MuiInputBase-input': { color: 'white' }, 
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
                  }}
                />
                <TextField
                  notched
                  label="Bankroll"
                  type="text"
                  value={bankroll}
                  onChange={e => {
                    const val = e.target.value;
                    const num = parseFloat(val);
                    setBankroll(isNaN(num) || num < 0 ? '' : num);
                  }}
                  onBlur={e => {
                    const val = e.target.value;
                    if (val === '' || isNaN(parseFloat(val)) || parseFloat(val) < 0) {
                      setBankroll(1000);
                    }
                  }}
                  fullWidth
                  size="small"
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography color="white">$</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    '& .MuiInputBase-input': { color: 'white' }, 
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
                  }}
                />
                <TextField
                  notched
                  label="Kelly Fraction"
                  type="text"
                  value={kellyFraction}
                  onChange={e => {
                    const val = e.target.value;
                    // Allow any input while typing, including decimals
                    setKellyFraction(val);
                  }}
                  onBlur={e => {
                    const val = e.target.value;
                    const num = parseFloat(val);
                    if (val === '' || isNaN(num) || num <= 0) {
                      setKellyFraction(1);
                    } else {
                      setKellyFraction(num);
                    }
                  }}
                  fullWidth
                  size="small"
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  sx={{ 
                    '& .MuiInputBase-input': { color: 'white' }, 
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
                  }}
                />
              </Box>
              <Divider sx={{ borderColor: '#444', mb: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: '#ffffff', fontSize: '0.875rem', mb: 1 }}>
                  Implied Odds Range (%)
                </Typography>
                <Slider
                  value={breakEvenWinRange}
                  onChange={handleSliderChange}
                  min={0}
                  max={100}
                  step={1}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: 0, label: '0' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 75, label: '75' },
                    { value: 100, label: '100' },
                  ]}
                  sx={{
                    mt: 2,
                    '& .MuiSlider-valueLabel': {
                      fontSize: '0.75rem',
                    },
                    '& .MuiSlider-markLabel': {
                      fontSize: '0.75rem',
                    },
                  }}
                />
              </Box>
              <Divider sx={{ borderColor: '#444', mb: 2 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hideTracked}
                    onChange={() => setHideTracked(h => !h)}
                    sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
                  />
                }
                label={<Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Hide Tracked Bets</Typography>}
                sx={{ mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showHiddenBets}
                    onChange={() => setShowHiddenBets(h => !h)}
                    sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
                  />
                }
                label={<Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Show Hidden Bets</Typography>}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hideMarket}
                    onChange={() => setHideMarket(h => !h)}
                    sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>
                      Hide Market
                    </Typography>
                    <Tooltip title="When checked, hiding a bet will also hide that bet for all other sportsbooks.">
                      <InfoIcon sx={{ fontSize: '16px', color: '#ffffff', ml: 0.5 }} />
                    </Tooltip>
                  </Box>
                }
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                <Button 
                  onClick={handleSettingsClose} Q
                  sx={{ 
                    color: '#ffffff', 
                    fontSize: '0.875rem', 
                    minWidth: '80px',
                    '&:hover': { backgroundColor: '#333333' },
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSettingsClose} 
                  sx={{ 
                    color: '#ff1744', 
                    fontSize: '0.875rem', 
                    minWidth: '80px',
                    '&:hover': { backgroundColor: '#333333' },
                  }}
                >
                  Save
                </Button>
              </Stack>
            </Box>

          </Popover>
            <GameLogModal
              open={gameLogModalOpen}
              onClose={handleCloseModal}
              playerData={selectedPlayerData}
              gameLogData={gameLogData}
              loading={gameLogLoading}
              sport={gameLogSport}
            />
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

const EVTool = (props) => {
  return <BetsDataGrid {...props} />;
};

export default EVTool;